/**
 * Settings Modal
 * Zeigt detaillierte Cookie-Einstellungen mit Kategorien und Services
 */
import { pmLog, pmError } from './Logger.js';

/**
 * Liefert true, wenn der Besucher unter Systemeinstellungen "Bewegungen
 * reduzieren" aktiviert hat. Siehe gleichlautende Funktion in
 * PixelmateBanner.js für den Hintergrund (WCAG 2.3.3, CSS-Media-Query allein
 * reicht nicht, da pixelmate.css im echten Frontend nicht geladen wird) sowie
 * für den Vorrang der Admin-Einstellung "Animationen für alle Besucher
 * deaktivieren" (pixelmate_force_reduced_motion).
 */
function prefersReducedMotion() {
    if (typeof window !== 'undefined' && window.pixelmateConfig?.settings?.forceReducedMotion) {
        return true;
    }

    return typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export class SettingsModal {
    // Mehrsprachige Fallback-Beschreibungen für bekannte Dienste (de/en/fr/es/it/nl/pl/da/sv)
    static SERVICE_DESCRIPTIONS = {
        google_analytics: { de: 'Webanalyse-Tool von Google',         en: 'Web analytics tool by Google',         fr: "Outil d'analyse Web de Google", es: 'Herramienta de analítica web de Google', it: 'Strumento di analisi web di Google', nl: 'Webanalysetool van Google', pl: 'Narzędzie do analizy stron internetowych firmy Google', da: 'Webanalyseværktøj fra Google', sv: 'Webbanalysverktyg från Google' },
        facebook_pixel:   { de: 'Marketing und Conversion-Tracking',  en: 'Marketing and conversion tracking',    fr: 'Marketing et suivi des conversions', es: 'Marketing y seguimiento de conversiones', it: 'Marketing e tracciamento delle conversioni', nl: 'Marketing en conversietracking', pl: 'Marketing i śledzenie konwersji', da: 'Marketing og konverteringssporing', sv: 'Marknadsföring och konverteringsspårning' },
        matomo:           { de: 'Selbst-gehostete Webanalyse',         en: 'Self-hosted web analytics',            fr: "Analyse Web auto-hébergée", es: 'Analítica web autoalojada', it: 'Analisi web self-hosted', nl: 'Zelf gehoste webanalyse', pl: 'Samodzielnie hostowana analiza stron internetowych', da: 'Selv-hostet webanalyse', sv: 'Självhostad webbanalys' },
        youtube:          { de: 'Video-Plattform',                     en: 'Video platform',                       fr: 'Plateforme vidéo', es: 'Plataforma de vídeo', it: 'Piattaforma video', nl: 'Videoplatform', pl: 'Platforma wideo', da: 'Videoplatform', sv: 'Videoplattform' },
        vimeo:            { de: 'Video-Plattform',                     en: 'Video platform',                       fr: 'Plateforme vidéo', es: 'Plataforma de vídeo', it: 'Piattaforma video', nl: 'Videoplatform', pl: 'Platforma wideo', da: 'Videoplatform', sv: 'Videoplattform' },
        dailymotion:      { de: 'Video-Plattform',                     en: 'Video platform',                       fr: 'Plateforme vidéo', es: 'Plataforma de vídeo', it: 'Piattaforma video', nl: 'Videoplatform', pl: 'Platforma wideo', da: 'Videoplatform', sv: 'Videoplattform' },
        spotify:          { de: 'Musik- und Podcast-Streaming',        en: 'Music and podcast streaming',          fr: 'Streaming de musique et de podcasts', es: 'Streaming de música y podcasts', it: 'Streaming di musica e podcast', nl: 'Muziek- en podcaststreaming', pl: 'Strumieniowanie muzyki i podcastów', da: 'Musik- og podcaststreaming', sv: 'Musik- och poddstreaming' },
        google_maps:      { de: 'Interaktive Karten',                  en: 'Interactive maps',                     fr: 'Cartes interactives', es: 'Mapas interactivos', it: 'Mappe interattive', nl: 'Interactieve kaarten', pl: 'Interaktywne mapy', da: 'Interaktive kort', sv: 'Interaktiva kartor' },
        soundcloud:       { de: 'Audio- und Musik-Streaming',          en: 'Audio and music streaming',            fr: 'Streaming audio et musical', es: 'Streaming de audio y música', it: 'Streaming audio e musicale', nl: 'Audio- en muziekstreaming', pl: 'Strumieniowanie audio i muzyki', da: 'Audio- og musikstreaming', sv: 'Ljud- och musikstreaming' },
    };

    // Wiederverwendbare Übersetzungen für Speicherdauer-Texte der unten in
    // NECESSARY_SERVICE_I18N hinterlegten Cookies (de/en/fr/es/it/nl/pl/da/sv).
    // "daysTemplate" ist ein Sonderfall für die dynamische Tagesangabe des
    // pixelmate_consent-Cookies (Platzhalter "%d" wird durch die tatsächliche
    // Anzahl Tage aus der Server-Antwort ersetzt, siehe loadServices()).
    static RETENTION_I18N = {
        session:              { de: 'Sitzung', en: 'Session', fr: 'Session', es: 'Sesión', it: 'Sessione', nl: 'Sessie', pl: 'Sesja', da: 'Session', sv: 'Session' },
        sessionOrLogout:      { de: 'Sitzung bzw. bis zum Abmelden', en: 'Session or until logout', fr: "Session ou jusqu'à la déconnexion", es: 'Sesión o hasta cerrar sesión', it: 'Sessione o fino al logout', nl: 'Sessie of tot uitloggen', pl: 'Sesja lub do wylogowania', da: 'Session eller indtil log ud', sv: 'Session eller till utloggning' },
        sessionOrUpTo1Year:   { de: 'Sitzung bzw. bis zu 1 Jahr', en: 'Session or up to 1 year', fr: "Session ou jusqu'à 1 an", es: 'Sesión o hasta 1 año', it: 'Sessione o fino a 1 anno', nl: 'Sessie of tot 1 jaar', pl: 'Sesja lub do 1 roku', da: 'Session eller op til 1 år', sv: 'Session eller upp till 1 år' },
        oneYear:              { de: '1 Jahr', en: '1 year', fr: '1 an', es: '1 año', it: '1 anno', nl: '1 jaar', pl: '1 rok', da: '1 år', sv: '1 år' },
        tenDays:              { de: '10 Tage', en: '10 days', fr: '10 jours', es: '10 días', it: '10 giorni', nl: '10 dagen', pl: '10 dni', da: '10 dage', sv: '10 dagar' },
        approx347Days:        { de: 'ca. 347 Tage', en: 'approx. 347 days', fr: 'environ 347 jours', es: 'aprox. 347 días', it: 'circa 347 giorni', nl: 'ca. 347 dagen', pl: 'ok. 347 dni', da: 'ca. 347 dage', sv: 'ca. 347 dagar' },
        thirtyMinutes:        { de: '30 Minuten', en: '30 minutes', fr: '30 minutes', es: '30 minutos', it: '30 minuti', nl: '30 minuten', pl: '30 minut', da: '30 minutter', sv: '30 minuter' },
        sixMonths:            { de: '6 Monate', en: '6 months', fr: '6 mois', es: '6 meses', it: '6 mesi', nl: '6 maanden', pl: '6 miesięcy', da: '6 måneder', sv: '6 månader' },
        twoDays:              { de: '2 Tage', en: '2 days', fr: '2 jours', es: '2 días', it: '2 giorni', nl: '2 dagen', pl: '2 dni', da: '2 dage', sv: '2 dagar' },
        daysTemplate:         { de: '%d Tage', en: '%d days', fr: '%d jours', es: '%d días', it: '%d giorni', nl: '%d dagen', pl: '%d dni', da: '%d dage', sv: '%d dagar' }
    };

    // Mehrsprachige Übersetzungen für die vom Server automatisch erkannten
    // "notwendigen" Dienste (siehe SettingsLocalizer::build_necessary_services_config())
    // und ihre Cookies. Deren Titel/Beschreibungen kommen serverseitig aus
    // PHP-__()-Aufrufen, die der WordPress-/WPML-/Polylang-Sprache der Seite
    // folgen - NICHT der hier per Sprach-Umschalter gewählten currentLang (siehe
    // Kommentar bei localizedDesc() weiter unten). Ohne diese Tabelle blieben
    // diese Dienste beim Umschalten auf eine andere Sprache immer in der
    // Server-Sprache stehen.
    static NECESSARY_SERVICE_I18N = {
        pixelmate_consent: {
            title: { de: 'Cookie-Einwilligung', en: 'Cookie consent', fr: 'Consentement aux cookies', es: 'Consentimiento de cookies', it: 'Consenso ai cookie', nl: 'Cookietoestemming', pl: 'Zgoda na pliki cookie', da: 'Cookie-samtykke', sv: 'Cookiesamtycke' },
            description: { de: 'Speichert Ihre Cookie-Auswahl, damit das Cookie-Banner nicht bei jedem Seitenaufruf erneut angezeigt wird.', en: "Stores your cookie selection so the cookie banner isn't shown again on every page view.", fr: "Enregistre votre choix de cookies afin que la bannière de cookies ne s'affiche pas à nouveau à chaque page vue.", es: 'Guarda su selección de cookies para que el banner de cookies no se muestre de nuevo en cada visita a la página.', it: 'Salva la selezione dei cookie in modo che il banner dei cookie non venga mostrato di nuovo a ogni visualizzazione della pagina.', nl: 'Slaat uw cookiekeuze op zodat de cookiebanner niet bij elke paginaweergave opnieuw wordt getoond.', pl: 'Zapisuje wybór plików cookie, aby baner cookie nie był wyświetlany ponownie przy każdym wyświetleniu strony.', da: 'Gemmer dit cookievalg, så cookiebanneret ikke vises igen ved hver sidevisning.', sv: 'Sparar ditt cookieval så att cookiebannern inte visas igen vid varje sidvisning.' },
            cookies: {
                pixelmate_consent: {
                    description: { de: 'Speichert die getroffene Cookie-Auswahl (Kategorien/Dienste) des Besuchers.', en: "Stores the visitor's cookie selection (categories/services).", fr: 'Enregistre le choix de cookies effectué par le visiteur (catégories/services).', es: 'Guarda la selección de cookies (categorías/servicios) realizada por el visitante.', it: 'Salva la selezione di cookie (categorie/servizi) effettuata dal visitatore.', nl: 'Slaat de door de bezoeker gemaakte cookiekeuze (categorieën/diensten) op.', pl: 'Zapisuje wybór plików cookie (kategorii/usług) dokonany przez odwiedzającego.', da: 'Gemmer besøgendes cookievalg (kategorier/tjenester).', sv: 'Sparar besökarens cookieval (kategorier/tjänster).' },
                    retentionTemplate: SettingsModal.RETENTION_I18N?.daysTemplate
                }
            }
        },
        wordpress_core: {
            title: { de: 'WordPress (Kernfunktionen)', en: 'WordPress (core functions)', fr: 'WordPress (fonctions principales)', es: 'WordPress (funciones principales)', it: 'WordPress (funzioni principali)', nl: 'WordPress (kernfuncties)', pl: 'WordPress (funkcje podstawowe)', da: 'WordPress (kernefunktioner)', sv: 'WordPress (kärnfunktioner)' },
            description: { de: 'Cookies des WordPress-Kerns für Login, Sitzungsverwaltung und grundlegende Funktionen der Website.', en: 'WordPress core cookies for login, session management and basic website functions.', fr: 'Cookies du cœur de WordPress pour la connexion, la gestion de session et les fonctions de base du site.', es: 'Cookies del núcleo de WordPress para el inicio de sesión, la gestión de sesiones y las funciones básicas del sitio web.', it: 'Cookie del core di WordPress per il login, la gestione delle sessioni e le funzioni di base del sito web.', nl: 'Cookies van de WordPress-kern voor inloggen, sessiebeheer en basisfuncties van de website.', pl: 'Pliki cookie jądra WordPressa do logowania, zarządzania sesją i podstawowych funkcji witryny.', da: 'WordPress-kernecookies til login, sessionshåndtering og grundlæggende webstedsfunktioner.', sv: 'WordPress kärncookies för inloggning, sessionshantering och grundläggande webbplatsfunktioner.' },
            cookies: {
                'wordpress_logged_in_*': {
                    description: { de: 'Speichert den Login-Status angemeldeter Benutzer.', en: 'Stores the login status of logged-in users.', fr: 'Enregistre le statut de connexion des utilisateurs connectés.', es: 'Guarda el estado de inicio de sesión de los usuarios registrados.', it: "Salva lo stato di accesso degli utenti che hanno effettuato il login.", nl: 'Slaat de inlogstatus van ingelogde gebruikers op.', pl: 'Zapisuje status zalogowania zalogowanych użytkowników.', da: 'Gemmer login-status for indloggede brugere.', sv: 'Sparar inloggningsstatus för inloggade användare.' },
                    retentionPeriod: SettingsModal.RETENTION_I18N?.sessionOrLogout
                },
                'wordpress_sec_*': {
                    description: { de: 'Sichert den Anmeldevorgang im WordPress-Administrationsbereich ab.', en: 'Secures the login process in the WordPress admin area.', fr: "Sécurise le processus de connexion dans l'espace d'administration de WordPress.", es: 'Protege el proceso de inicio de sesión en el área de administración de WordPress.', it: "Protegge il processo di accesso nell'area di amministrazione di WordPress.", nl: 'Beveiligt het inlogproces in het WordPress-beheergedeelte.', pl: 'Zabezpiecza proces logowania w panelu administracyjnym WordPressa.', da: 'Sikrer login-processen i WordPress-administrationsområdet.', sv: 'Skyddar inloggningsprocessen i WordPress-administrationsområdet.' },
                    retentionPeriod: SettingsModal.RETENTION_I18N?.sessionOrLogout
                },
                wordpress_test_cookie: {
                    description: { de: 'Prüft, ob der Browser des Besuchers Cookies akzeptiert.', en: "Checks whether the visitor's browser accepts cookies.", fr: 'Vérifie si le navigateur du visiteur accepte les cookies.', es: 'Comprueba si el navegador del visitante acepta cookies.', it: 'Verifica se il browser del visitatore accetta i cookie.', nl: 'Controleert of de browser van de bezoeker cookies accepteert.', pl: 'Sprawdza, czy przeglądarka odwiedzającego akceptuje pliki cookie.', da: 'Kontrollerer, om den besøgendes browser accepterer cookies.', sv: 'Kontrollerar om besökarens webbläsare accepterar cookies.' },
                    retentionPeriod: SettingsModal.RETENTION_I18N?.session
                },
                'wp-settings-*': {
                    description: { de: 'Speichert individuelle Anpassungen der WordPress-Administrationsoberfläche.', en: 'Stores individual customizations of the WordPress admin interface.', fr: "Enregistre les personnalisations individuelles de l'interface d'administration de WordPress.", es: 'Guarda las personalizaciones individuales de la interfaz de administración de WordPress.', it: "Salva le personalizzazioni individuali dell'interfaccia di amministrazione di WordPress.", nl: 'Slaat individuele aanpassingen van de WordPress-beheerinterface op.', pl: 'Zapisuje indywidualne dostosowania interfejsu administracyjnego WordPressa.', da: 'Gemmer individuelle tilpasninger af WordPress-administrationsgrænsefladen.', sv: 'Sparar individuella anpassningar av WordPress-administrationsgränssnittet.' },
                    retentionPeriod: SettingsModal.RETENTION_I18N?.oneYear
                },
                'wp-settings-time-*': {
                    description: { de: 'Speichert den Zeitpunkt der letzten Anpassung der Administrationsoberfläche.', en: 'Stores the time of the last change to the admin interface.', fr: "Enregistre l'heure de la dernière modification de l'interface d'administration.", es: 'Guarda la hora de la última modificación de la interfaz de administración.', it: "Salva il momento dell'ultima modifica dell'interfaccia di amministrazione.", nl: 'Slaat het tijdstip van de laatste aanpassing van de beheerinterface op.', pl: 'Zapisuje moment ostatniej zmiany interfejsu administracyjnego.', da: 'Gemmer tidspunktet for den seneste ændring af administrationsgrænsefladen.', sv: 'Sparar tidpunkten för den senaste ändringen av administrationsgränssnittet.' },
                    retentionPeriod: SettingsModal.RETENTION_I18N?.oneYear
                },
                'wp-postpass_*': {
                    description: { de: 'Speichert den Zugriff auf passwortgeschützte Beiträge und Seiten.', en: 'Stores access to password-protected posts and pages.', fr: "Enregistre l'accès aux articles et pages protégés par mot de passe.", es: 'Guarda el acceso a entradas y páginas protegidas con contraseña.', it: "Salva l'accesso a articoli e pagine protetti da password.", nl: "Slaat toegang tot met een wachtwoord beveiligde berichten en pagina's op.", pl: 'Zapisuje dostęp do wpisów i stron chronionych hasłem.', da: 'Gemmer adgangen til adgangskodebeskyttede indlæg og sider.', sv: 'Sparar åtkomsten till lösenordsskyddade inlägg och sidor.' },
                    retentionPeriod: SettingsModal.RETENTION_I18N?.tenDays
                },
                'comment_author_*': {
                    description: { de: 'Speichert Name, E-Mail-Adresse und Website für künftige Kommentare.', en: 'Stores name, email address and website for future comments.', fr: "Enregistre le nom, l'adresse e-mail et le site web pour les futurs commentaires.", es: 'Guarda el nombre, la dirección de correo electrónico y el sitio web para futuros comentarios.', it: 'Salva nome, indirizzo e-mail e sito web per i commenti futuri.', nl: 'Slaat naam, e-mailadres en website op voor toekomstige reacties.', pl: 'Zapisuje imię, adres e-mail i witrynę na potrzeby przyszłych komentarzy.', da: 'Gemmer navn, e-mailadresse og websted til fremtidige kommentarer.', sv: 'Sparar namn, e-postadress och webbplats för framtida kommentarer.' },
                    retentionPeriod: SettingsModal.RETENTION_I18N?.approx347Days
                }
            }
        },
        woocommerce: {
            title: { de: 'WooCommerce', en: 'WooCommerce', fr: 'WooCommerce', es: 'WooCommerce', it: 'WooCommerce', nl: 'WooCommerce', pl: 'WooCommerce', da: 'WooCommerce', sv: 'WooCommerce' },
            description: { de: 'Cookies von WooCommerce zur Verwaltung von Warenkorb und Bestellvorgang.', en: 'WooCommerce cookies for managing the shopping cart and checkout process.', fr: 'Cookies de WooCommerce pour la gestion du panier et du processus de commande.', es: 'Cookies de WooCommerce para gestionar el carrito y el proceso de pedido.', it: "Cookie di WooCommerce per la gestione del carrello e del processo d'ordine.", nl: 'Cookies van WooCommerce voor het beheren van winkelwagen en bestelproces.', pl: 'Pliki cookie WooCommerce do zarządzania koszykiem i procesem zamówienia.', da: 'WooCommerce-cookies til administration af indkøbskurv og bestillingsproces.', sv: 'WooCommerce-cookies för hantering av varukorg och beställningsprocess.' },
            cookies: {
                woocommerce_cart_hash: {
                    description: { de: 'Erkennt Änderungen am Inhalt des Warenkorbs.', en: 'Detects changes to the contents of the shopping cart.', fr: 'Détecte les modifications du contenu du panier.', es: 'Detecta cambios en el contenido del carrito.', it: 'Rileva le modifiche al contenuto del carrello.', nl: 'Detecteert wijzigingen in de inhoud van de winkelwagen.', pl: 'Wykrywa zmiany w zawartości koszyka.', da: 'Registrerer ændringer i indkøbskurvens indhold.', sv: 'Upptäcker ändringar i varukorgens innehåll.' },
                    retentionPeriod: SettingsModal.RETENTION_I18N?.session
                },
                woocommerce_items_in_cart: {
                    description: { de: 'Speichert, ob sich Artikel im Warenkorb befinden.', en: 'Stores whether there are items in the shopping cart.', fr: 'Enregistre si des articles se trouvent dans le panier.', es: 'Guarda si hay artículos en el carrito.', it: 'Salva se sono presenti articoli nel carrello.', nl: 'Slaat op of er artikelen in de winkelwagen zitten.', pl: 'Zapisuje, czy w koszyku znajdują się produkty.', da: 'Gemmer, om der er varer i indkøbskurven.', sv: 'Sparar om det finns artiklar i varukorgen.' },
                    retentionPeriod: SettingsModal.RETENTION_I18N?.session
                },
                'wp_woocommerce_session_*': {
                    description: { de: 'Speichert eine eindeutige Sitzungs-ID zur Identifikation des Warenkorbs.', en: 'Stores a unique session ID to identify the shopping cart.', fr: 'Enregistre un identifiant de session unique pour identifier le panier.', es: 'Guarda un ID de sesión único para identificar el carrito.', it: 'Salva un ID di sessione univoco per identificare il carrello.', nl: 'Slaat een unieke sessie-ID op om de winkelwagen te identificeren.', pl: 'Zapisuje unikalny identyfikator sesji do identyfikacji koszyka.', da: 'Gemmer et unikt sessions-id til identifikation af indkøbskurven.', sv: 'Sparar ett unikt sessions-ID för att identifiera varukorgen.' },
                    retentionPeriod: SettingsModal.RETENTION_I18N?.twoDays
                },
                'store_notice*': {
                    description: { de: 'Merkt sich, ob ein Hinweis im Shop bereits geschlossen wurde.', en: 'Remembers whether a notice in the shop has already been dismissed.', fr: "Se souvient si un avis de la boutique a déjà été fermé.", es: 'Recuerda si un aviso de la tienda ya se ha cerrado.', it: 'Ricorda se un avviso del negozio è già stato chiuso.', nl: 'Onthoudt of een melding in de winkel al is gesloten.', pl: 'Zapamiętuje, czy powiadomienie w sklepie zostało już zamknięte.', da: 'Husker, om en meddelelse i butikken allerede er blevet lukket.', sv: 'Kommer ihåg om ett meddelande i butiken redan har stängts.' },
                    retentionPeriod: SettingsModal.RETENTION_I18N?.session
                }
            }
        },
        woocommerce_germanized: {
            title: { de: 'WooCommerce Germanized', en: 'WooCommerce Germanized', fr: 'WooCommerce Germanized', es: 'WooCommerce Germanized', it: 'WooCommerce Germanized', nl: 'WooCommerce Germanized', pl: 'WooCommerce Germanized', da: 'WooCommerce Germanized', sv: 'WooCommerce Germanized' },
            description: { de: 'Erweitert WooCommerce um rechtliche Funktionen (z. B. Widerrufsbelehrung, Rechtstexte) und nutzt dabei dieselben technisch notwendigen Warenkorb- und Sitzungs-Cookies wie WooCommerce.', en: 'Extends WooCommerce with legal functions (e.g. right of withdrawal notice, legal texts) and uses the same technically necessary cart and session cookies as WooCommerce.', fr: "Étend WooCommerce avec des fonctions juridiques (p. ex. information sur le droit de rétractation, mentions légales) et utilise les mêmes cookies de panier et de session techniquement nécessaires que WooCommerce.", es: 'Amplía WooCommerce con funciones legales (p. ej., información sobre el derecho de desistimiento, textos legales) y utiliza las mismas cookies de carrito y sesión técnicamente necesarias que WooCommerce.', it: 'Estende WooCommerce con funzioni legali (ad es. informativa sul diritto di recesso, testi legali) e utilizza gli stessi cookie di carrello e sessione tecnicamente necessari di WooCommerce.', nl: 'Breidt WooCommerce uit met juridische functies (bijv. herroepingsinstructie, juridische teksten) en gebruikt daarbij dezelfde technisch noodzakelijke winkelwagen- en sessiecookies als WooCommerce.', pl: 'Rozszerza WooCommerce o funkcje prawne (np. informację o prawie odstąpienia od umowy, teksty prawne) i wykorzystuje te same technicznie niezbędne pliki cookie koszyka i sesji, co WooCommerce.', da: 'Udvider WooCommerce med juridiske funktioner (f.eks. oplysning om fortrydelsesret, juridiske tekster) og bruger de samme teknisk nødvendige indkøbskurv- og sessionscookies som WooCommerce.', sv: 'Utökar WooCommerce med juridiska funktioner (t.ex. information om ångerrätt, juridiska texter) och använder samma tekniskt nödvändiga varukorgs- och sessionscookies som WooCommerce.' },
            cookies: {}
        },
        easy_digital_downloads: {
            title: { de: 'Easy Digital Downloads', en: 'Easy Digital Downloads', fr: 'Easy Digital Downloads', es: 'Easy Digital Downloads', it: 'Easy Digital Downloads', nl: 'Easy Digital Downloads', pl: 'Easy Digital Downloads', da: 'Easy Digital Downloads', sv: 'Easy Digital Downloads' },
            description: { de: 'Cookies von Easy Digital Downloads zur Verwaltung von Warenkorb und Bestellvorgang beim Verkauf digitaler Produkte.', en: 'Easy Digital Downloads cookies for managing the shopping cart and checkout process when selling digital products.', fr: "Cookies d'Easy Digital Downloads pour la gestion du panier et du processus de commande lors de la vente de produits numériques.", es: 'Cookies de Easy Digital Downloads para gestionar el carrito y el proceso de pedido en la venta de productos digitales.', it: "Cookie di Easy Digital Downloads per la gestione del carrello e del processo d'ordine nella vendita di prodotti digitali.", nl: 'Cookies van Easy Digital Downloads voor het beheren van winkelwagen en bestelproces bij de verkoop van digitale producten.', pl: 'Pliki cookie Easy Digital Downloads do zarządzania koszykiem i procesem zamówienia przy sprzedaży produktów cyfrowych.', da: 'Easy Digital Downloads-cookies til administration af indkøbskurv og bestillingsproces ved salg af digitale produkter.', sv: 'Easy Digital Downloads-cookies för hantering av varukorg och beställningsprocess vid försäljning av digitala produkter.' },
            cookies: {
                edd_items_in_cart: {
                    description: { de: 'Speichert, welche Produkte sich aktuell im Warenkorb befinden.', en: 'Stores which products are currently in the shopping cart.', fr: 'Enregistre les produits actuellement présents dans le panier.', es: 'Guarda qué productos hay actualmente en el carrito.', it: 'Salva quali prodotti sono attualmente presenti nel carrello.', nl: 'Slaat op welke producten zich momenteel in de winkelwagen bevinden.', pl: 'Zapisuje, jakie produkty znajdują się aktualnie w koszyku.', da: 'Gemmer, hvilke produkter der aktuelt er i indkøbskurven.', sv: 'Sparar vilka produkter som just nu finns i varukorgen.' },
                    retentionPeriod: SettingsModal.RETENTION_I18N?.session
                },
                edd_wp_session: {
                    description: { de: 'Speichert eine eindeutige Sitzungs-ID zur Verwaltung des Bestellvorgangs.', en: 'Stores a unique session ID to manage the checkout process.', fr: 'Enregistre un identifiant de session unique pour gérer le processus de commande.', es: 'Guarda un ID de sesión único para gestionar el proceso de pedido.', it: "Salva un ID di sessione univoco per gestire il processo d'ordine.", nl: 'Slaat een unieke sessie-ID op om het bestelproces te beheren.', pl: 'Zapisuje unikalny identyfikator sesji do zarządzania procesem zamówienia.', da: 'Gemmer et unikt sessions-id til at administrere bestillingsprocessen.', sv: 'Sparar ett unikt sessions-ID för att hantera beställningsprocessen.' },
                    retentionPeriod: SettingsModal.RETENTION_I18N?.session
                },
                edd_purchase_session: {
                    description: { de: 'Speichert den Status eines laufenden Kaufvorgangs.', en: 'Stores the status of an ongoing purchase.', fr: "Enregistre l'état d'un processus d'achat en cours.", es: 'Guarda el estado de una compra en curso.', it: 'Salva lo stato di un acquisto in corso.', nl: 'Slaat de status van een lopende aankoop op.', pl: 'Zapisuje status trwającej transakcji zakupu.', da: 'Gemmer status for en igangværende købsproces.', sv: 'Sparar status för ett pågående köp.' },
                    retentionPeriod: SettingsModal.RETENTION_I18N?.session
                }
            }
        },
        memberpress: {
            title: { de: 'MemberPress', en: 'MemberPress', fr: 'MemberPress', es: 'MemberPress', it: 'MemberPress', nl: 'MemberPress', pl: 'MemberPress', da: 'MemberPress', sv: 'MemberPress' },
            description: { de: 'Cookies von MemberPress zur Verwaltung von Mitgliedschaften und Zugriffsrechten.', en: 'MemberPress cookies for managing memberships and access rights.', fr: "Cookies de MemberPress pour la gestion des adhésions et des droits d'accès.", es: 'Cookies de MemberPress para gestionar membresías y derechos de acceso.', it: 'Cookie di MemberPress per la gestione degli abbonamenti e dei diritti di accesso.', nl: 'Cookies van MemberPress voor het beheren van lidmaatschappen en toegangsrechten.', pl: 'Pliki cookie MemberPress do zarządzania członkostwami i prawami dostępu.', da: 'MemberPress-cookies til administration af medlemskaber og adgangsrettigheder.', sv: 'MemberPress-cookies för hantering av medlemskap och åtkomsträttigheter.' },
            cookies: {
                mp_session: {
                    description: { de: 'Speichert Sitzungsdaten zu Mitgliedschaftsstatus und Zugriffsrechten eingeloggter Mitglieder.', en: 'Stores session data on membership status and access rights of logged-in members.', fr: "Enregistre les données de session relatives au statut d'adhésion et aux droits d'accès des membres connectés.", es: 'Guarda datos de sesión sobre el estado de membresía y los derechos de acceso de los miembros conectados.', it: "Salva i dati di sessione relativi allo stato dell'abbonamento e ai diritti di accesso dei membri collegati.", nl: 'Slaat sessiegegevens op over lidmaatschapsstatus en toegangsrechten van ingelogde leden.', pl: 'Zapisuje dane sesji dotyczące statusu członkostwa i praw dostępu zalogowanych członków.', da: 'Gemmer sessionsdata om medlemskabsstatus og adgangsrettigheder for indloggede medlemmer.', sv: 'Sparar sessionsdata om medlemskapsstatus och åtkomsträttigheter för inloggade medlemmar.' },
                    retentionPeriod: SettingsModal.RETENTION_I18N?.session
                }
            }
        },
        learndash: {
            title: { de: 'LearnDash', en: 'LearnDash', fr: 'LearnDash', es: 'LearnDash', it: 'LearnDash', nl: 'LearnDash', pl: 'LearnDash', da: 'LearnDash', sv: 'LearnDash' },
            description: { de: 'Cookies von LearnDash zur Speicherung des Lernfortschritts in Online-Kursen.', en: 'LearnDash cookies for storing learning progress in online courses.', fr: "Cookies de LearnDash pour enregistrer la progression d'apprentissage dans les cours en ligne.", es: 'Cookies de LearnDash para guardar el progreso de aprendizaje en los cursos en línea.', it: "Cookie di LearnDash per salvare l'avanzamento dell'apprendimento nei corsi online.", nl: 'Cookies van LearnDash voor het opslaan van de leervooruitgang in online cursussen.', pl: 'Pliki cookie LearnDash do zapisywania postępów w nauce w kursach online.', da: 'LearnDash-cookies til at gemme læringsfremskridt i onlinekurser.', sv: 'LearnDash-cookies för att spara inlärningsframsteg i onlinekurser.' },
            cookies: {
                'learndash_*': {
                    description: { de: 'Speichert den Lernfortschritt und Quiz-Status innerhalb eines Kurses.', en: 'Stores learning progress and quiz status within a course.', fr: "Enregistre la progression d'apprentissage et l'état des quiz au sein d'un cours.", es: 'Guarda el progreso de aprendizaje y el estado de los cuestionarios dentro de un curso.', it: "Salva l'avanzamento dell'apprendimento e lo stato dei quiz all'interno di un corso.", nl: 'Slaat de leervooruitgang en quizstatus binnen een cursus op.', pl: 'Zapisuje postępy w nauce i status quizów w ramach kursu.', da: 'Gemmer læringsfremskridt og quizstatus i et kursus.', sv: 'Sparar inlärningsframsteg och quizstatus inom en kurs.' },
                    retentionPeriod: SettingsModal.RETENTION_I18N?.sessionOrUpTo1Year
                }
            }
        },
        turnstile: {
            title: { de: 'Cloudflare Turnstile', en: 'Cloudflare Turnstile', fr: 'Cloudflare Turnstile', es: 'Cloudflare Turnstile', it: 'Cloudflare Turnstile', nl: 'Cloudflare Turnstile', pl: 'Cloudflare Turnstile', da: 'Cloudflare Turnstile', sv: 'Cloudflare Turnstile' },
            description: { de: 'Turnstile ist extern eingebunden (z. B. über ein Formular-Plugin) und lädt unabhängig von der Cookie-Einwilligung eine Verbindung zu Cloudflare, um Formulare vor Spam und Bots zu schützen.', en: 'Turnstile is embedded externally (e.g. via a form plugin) and loads a connection to Cloudflare independently of cookie consent, to protect forms from spam and bots.', fr: "Turnstile est intégré de manière externe (p. ex. via un plugin de formulaire) et charge une connexion à Cloudflare indépendamment du consentement aux cookies, afin de protéger les formulaires contre le spam et les robots.", es: 'Turnstile está integrado externamente (p. ej., a través de un plugin de formularios) y carga una conexión con Cloudflare independientemente del consentimiento de cookies, para proteger los formularios contra el spam y los bots.', it: 'Turnstile è integrato esternamente (ad es. tramite un plugin per moduli) e carica una connessione a Cloudflare indipendentemente dal consenso ai cookie, per proteggere i moduli da spam e bot.', nl: 'Turnstile is extern geïntegreerd (bijv. via een formulierplugin) en laadt onafhankelijk van de cookietoestemming een verbinding met Cloudflare om formulieren te beschermen tegen spam en bots.', pl: 'Turnstile jest zintegrowany zewnętrznie (np. za pomocą wtyczki formularza) i niezależnie od zgody na pliki cookie ładuje połączenie z Cloudflare, aby chronić formularze przed spamem i botami.', da: 'Turnstile er eksternt integreret (f.eks. via et formular-plugin) og indlæser en forbindelse til Cloudflare uafhængigt af cookie-samtykket for at beskytte formularer mod spam og bots.', sv: 'Turnstile är externt inbäddat (t.ex. via ett formulärtillägg) och laddar en anslutning till Cloudflare oberoende av cookiesamtycket för att skydda formulär mot spam och bottar.' },
            cookies: {
                cf_clearance: {
                    description: { de: 'Von Cloudflare Turnstile gesetzt, um einen erfolgreich abgeschlossenen Sicherheitscheck zu bestätigen und Mensch von Bot zu unterscheiden.', en: 'Set by Cloudflare Turnstile to confirm a successfully completed security check and distinguish humans from bots.', fr: 'Défini par Cloudflare Turnstile pour confirmer une vérification de sécurité réussie et distinguer les humains des robots.', es: 'Establecida por Cloudflare Turnstile para confirmar una verificación de seguridad completada con éxito y distinguir entre humanos y bots.', it: 'Impostato da Cloudflare Turnstile per confermare un controllo di sicurezza completato con successo e distinguere gli esseri umani dai bot.', nl: 'Ingesteld door Cloudflare Turnstile om een succesvol afgeronde beveiligingscontrole te bevestigen en mensen van bots te onderscheiden.', pl: 'Ustawiany przez Cloudflare Turnstile w celu potwierdzenia pomyślnie zakończonej kontroli bezpieczeństwa i odróżnienia człowieka od bota.', da: 'Sat af Cloudflare Turnstile for at bekræfte en gennemført sikkerhedskontrol og skelne mellem mennesker og bots.', sv: 'Sätts av Cloudflare Turnstile för att bekräfta en genomförd säkerhetskontroll och skilja människor från bottar.' },
                    retentionPeriod: SettingsModal.RETENTION_I18N?.thirtyMinutes
                }
            }
        },
        recaptcha: {
            title: { de: 'Google reCAPTCHA', en: 'Google reCAPTCHA', fr: 'Google reCAPTCHA', es: 'Google reCAPTCHA', it: 'Google reCAPTCHA', nl: 'Google reCAPTCHA', pl: 'Google reCAPTCHA', da: 'Google reCAPTCHA', sv: 'Google reCAPTCHA' },
            description: { de: 'reCAPTCHA ist extern eingebunden (z. B. über ein Formular-Plugin) und lädt unabhängig von der Cookie-Einwilligung eine Verbindung zu Google, um Formulare vor Spam und Bots zu schützen.', en: 'reCAPTCHA is embedded externally (e.g. via a form plugin) and loads a connection to Google independently of cookie consent, to protect forms from spam and bots.', fr: "reCAPTCHA est intégré de manière externe (p. ex. via un plugin de formulaire) et charge une connexion à Google indépendamment du consentement aux cookies, afin de protéger les formulaires contre le spam et les robots.", es: 'reCAPTCHA está integrado externamente (p. ej., a través de un plugin de formularios) y carga una conexión con Google independientemente del consentimiento de cookies, para proteger los formularios contra el spam y los bots.', it: 'reCAPTCHA è integrato esternamente (ad es. tramite un plugin per moduli) e carica una connessione a Google indipendentemente dal consenso ai cookie, per proteggere i moduli da spam e bot.', nl: 'reCAPTCHA is extern geïntegreerd (bijv. via een formulierplugin) en laadt onafhankelijk van de cookietoestemming een verbinding met Google om formulieren te beschermen tegen spam en bots.', pl: 'reCAPTCHA jest zintegrowana zewnętrznie (np. za pomocą wtyczki formularza) i niezależnie od zgody na pliki cookie ładuje połączenie z Google, aby chronić formularze przed spamem i botami.', da: 'reCAPTCHA er eksternt integreret (f.eks. via et formular-plugin) og indlæser en forbindelse til Google uafhængigt af cookie-samtykket for at beskytte formularer mod spam og bots.', sv: 'reCAPTCHA är externt inbäddat (t.ex. via ett formulärtillägg) och laddar en anslutning till Google oberoende av cookiesamtycket för att skydda formulär mot spam och bottar.' },
            cookies: {
                _GRECAPTCHA: {
                    description: { de: 'Von Google reCAPTCHA gesetzt, um das Formular vor automatisiertem Spam zu schützen und Mensch von Bot zu unterscheiden.', en: 'Set by Google reCAPTCHA to protect the form from automated spam and distinguish humans from bots.', fr: 'Défini par Google reCAPTCHA pour protéger le formulaire contre le spam automatisé et distinguer les humains des robots.', es: 'Establecida por Google reCAPTCHA para proteger el formulario contra el spam automatizado y distinguir entre humanos y bots.', it: 'Impostato da Google reCAPTCHA per proteggere il modulo dallo spam automatizzato e distinguere gli esseri umani dai bot.', nl: 'Ingesteld door Google reCAPTCHA om het formulier te beschermen tegen geautomatiseerde spam en mensen van bots te onderscheiden.', pl: 'Ustawiany przez Google reCAPTCHA w celu ochrony formularza przed zautomatyzowanym spamem i odróżnienia człowieka od bota.', da: 'Sat af Google reCAPTCHA for at beskytte formularen mod automatiseret spam og skelne mellem mennesker og bots.', sv: 'Sätts av Google reCAPTCHA för att skydda formuläret mot automatiserad spam och skilja människor från bottar.' },
                    retentionPeriod: SettingsModal.RETENTION_I18N?.sixMonths
                }
            }
        }
    };

    // Ergänzende Anbieter-/Datenschutz-Infos und Default-Kategorie für Dienste,
    // die im Integrationsmanager aktiviert werden können, aber keine (oder keine
    // vollständigen) Metadaten vom Server mitbekommen (siehe SettingsLocalizer::
    // build_active_services_config()). Dienste mit eigener Kategorie vom Server
    // (z.B. automatisch erkannte Script-Dienste) nutzen `category` nur als Fallback.
    static EXTRA_SERVICE_INFO = {
        google_ads: { name: 'Google Ads', category: 'marketing', provider: 'Google LLC', privacy_url: 'https://policies.google.com/privacy', description: { de: 'Conversion-Tracking und Remarketing für Google-Werbeanzeigen', en: 'Conversion tracking and remarketing for Google ads', fr: 'Suivi des conversions et remarketing pour les annonces Google', es: 'Seguimiento de conversiones y remarketing para anuncios de Google', it: 'Tracciamento delle conversioni e remarketing per gli annunci Google', nl: 'Conversietracking en remarketing voor Google-advertenties', pl: 'Śledzenie konwersji i remarketing dla reklam Google', da: 'Konverteringssporing og remarketing til Google-annoncer', sv: 'Konverteringsspårning och remarketing för Google-annonser' } },
        google_tag_manager: { name: 'Google Tag Manager', category: 'marketing', provider: 'Google LLC', privacy_url: 'https://policies.google.com/privacy', description: { de: 'Zentrale Tag-Verwaltung für Marketing-Tools', en: 'Central tag management for marketing tools', fr: 'Gestion centralisée des balises pour les outils marketing', es: 'Gestión centralizada de etiquetas para herramientas de marketing', it: 'Gestione centralizzata dei tag per gli strumenti di marketing', nl: 'Centraal tagbeheer voor marketingtools', pl: 'Centralne zarządzanie tagami dla narzędzi marketingowych', da: 'Central tag-administration til marketingværktøjer', sv: 'Central tagghantering för marknadsföringsverktyg' } },
        pinterest_pixel: { name: 'Pinterest Tag', category: 'marketing', provider: 'Pinterest Inc.', privacy_url: 'https://policy.pinterest.com/de/privacy-policy', description: { de: 'Conversion-Tracking für Pinterest-Werbeanzeigen', en: 'Conversion tracking for Pinterest ads', fr: 'Suivi des conversions pour les annonces Pinterest', es: 'Seguimiento de conversiones para anuncios de Pinterest', it: 'Tracciamento delle conversioni per gli annunci Pinterest', nl: 'Conversietracking voor Pinterest-advertenties', pl: 'Śledzenie konwersji dla reklam Pinterest', da: 'Konverteringssporing til Pinterest-annoncer', sv: 'Konverteringsspårning för Pinterest-annonser' } },
        pinterest: { name: 'Pinterest', category: 'external', provider: 'Pinterest Inc.', privacy_url: 'https://policy.pinterest.com/de/privacy-policy', description: { de: 'Plattform zum Teilen und Entdecken von Bildern', en: 'Platform for sharing and discovering images', fr: "Plateforme de partage et de découverte d'images", es: 'Plataforma para compartir y descubrir imágenes', it: 'Piattaforma per condividere e scoprire immagini', nl: 'Platform voor het delen en ontdekken van afbeeldingen', pl: 'Platforma do udostępniania i odkrywania obrazów', da: 'Platform til deling og opdagelse af billeder', sv: 'Plattform för att dela och upptäcka bilder' } },
        twitter: { name: 'Twitter / X', category: 'external', provider: 'X Corp.', privacy_url: 'https://twitter.com/de/privacy', description: { de: 'Soziales Netzwerk und Kurznachrichtendienst', en: 'Social network and microblogging service', fr: 'Réseau social et service de microblogging', es: 'Red social y servicio de microblogging', it: 'Social network e servizio di microblogging', nl: 'Sociaal netwerk en microblogdienst', pl: 'Sieć społecznościowa i usługa mikroblogowania', da: 'Socialt netværk og mikroblogtjeneste', sv: 'Socialt nätverk och mikrobloggtjänst' } },
        instagram: { name: 'Instagram', category: 'external', provider: 'Meta Platforms Inc.', privacy_url: 'https://www.instagram.com/legal/privacy/', description: { de: 'Plattform zum Teilen von Fotos und Videos', en: 'Platform for sharing photos and videos', fr: 'Plateforme de partage de photos et de vidéos', es: 'Plataforma para compartir fotos y vídeos', it: 'Piattaforma per condividere foto e video', nl: 'Platform voor het delen van foto\'s en video\'s', pl: 'Platforma do udostępniania zdjęć i filmów', da: 'Platform til deling af billeder og videoer', sv: 'Plattform för att dela foton och videor' } },
        threads: { name: 'Threads', category: 'external', provider: 'Meta Platforms Inc.', privacy_url: 'https://www.threads.net/privacy', description: { de: 'Soziales Netzwerk von Meta für Textbeiträge', en: "Meta's social network for text posts", fr: 'Réseau social de Meta pour les publications textuelles', es: 'Red social de Meta para publicaciones de texto', it: 'Social network di Meta per post testuali', nl: 'Sociaal netwerk van Meta voor tekstberichten', pl: 'Sieć społecznościowa Meta do publikowania wpisów tekstowych', da: 'Metas sociale netværk til tekstopslag', sv: 'Metas sociala nätverk för textinlägg' } },
        podigee: { name: 'Podigee', category: 'external', provider: 'Podigee GmbH', privacy_url: 'https://www.podigee.com/de/datenschutz', description: { de: 'Podcast-Hosting- und Player-Plattform', en: 'Podcast hosting and player platform', fr: "Plateforme d'hébergement et de lecteur de podcasts", es: 'Plataforma de alojamiento y reproductor de podcasts', it: 'Piattaforma di hosting e player per podcast', nl: 'Podcasthosting- en spelerplatform', pl: 'Platforma hostingowa i odtwarzacz podcastów', da: 'Podcast-hosting- og afspilningsplatform', sv: 'Poddhosting- och spelarplattform' } },
        deezer: { name: 'Deezer', category: 'external', provider: 'Deezer S.A.', privacy_url: 'https://www.deezer.com/legal/personal-datas', description: { de: 'Musik- und Podcast-Streaming-Plattform', en: 'Music and podcast streaming platform', fr: 'Plateforme de streaming de musique et de podcasts', es: 'Plataforma de streaming de música y podcasts', it: 'Piattaforma di streaming musicale e di podcast', nl: 'Muziek- en podcaststreamingplatform', pl: 'Platforma do strumieniowania muzyki i podcastów', da: 'Platform til musik- og podcaststreaming', sv: 'Plattform för musik- och poddstreaming' } },
        acast: { name: 'Acast', category: 'external', provider: 'Acast AB', privacy_url: 'https://www.acast.com/privacy', description: { de: 'Podcast-Hosting- und Player-Plattform', en: 'Podcast hosting and player platform', fr: "Plateforme d'hébergement et de lecteur de podcasts", es: 'Plataforma de alojamiento y reproductor de podcasts', it: 'Piattaforma di hosting e player per podcast', nl: 'Podcasthosting- en spelerplatform', pl: 'Platforma hostingowa i odtwarzacz podcastów', da: 'Podcast-hosting- og afspilningsplatform', sv: 'Poddhosting- och spelarplattform' } },
        calendly: { name: 'Calendly', category: 'external', provider: 'Calendly LLC', privacy_url: 'https://calendly.com/privacy', description: { de: 'Online-Terminbuchungstool', en: 'Online appointment scheduling tool', fr: 'Outil de prise de rendez-vous en ligne', es: 'Herramienta de programación de citas en línea', it: 'Strumento online per la prenotazione di appuntamenti', nl: 'Online tool voor het maken van afspraken', pl: 'Narzędzie do umawiania spotkań online', da: 'Online værktøj til aftalebestilling', sv: 'Onlineverktyg för bokning av möten' } },
        open_street_map: { name: 'OpenStreetMap', category: 'external', provider: 'OpenStreetMap Foundation', privacy_url: 'https://wiki.osmfoundation.org/wiki/Privacy_Policy', description: { de: 'Freies, gemeinschaftlich erstelltes Kartenmaterial', en: 'Free, community-created map data', fr: 'Données cartographiques libres et collaboratives', es: 'Datos de mapas libres y colaborativos', it: 'Dati cartografici liberi e collaborativi', nl: 'Vrij, door de gemeenschap gemaakt kaartmateriaal', pl: 'Bezpłatne dane mapowe tworzone przez społeczność', da: 'Frit kortmateriale skabt af fællesskabet', sv: 'Fritt, gemenskapsskapat kartmaterial' } },
        wordpress_stats: { name: 'WordPress.com Statistiken', category: 'analytics', provider: 'Automattic Inc.', privacy_url: 'https://automattic.com/privacy/' },
        gravatar: { name: 'Gravatar', category: 'external', provider: 'Automattic Inc.', privacy_url: 'https://automattic.com/privacy/' },
        vg_wort: { name: 'VG WORT Zählmarken', category: 'necessary', provider: 'VG WORT', privacy_url: 'https://www.vgwort.de/datenschutz.html', description: { de: 'Zählmarken zur Erfassung von Textzugriffen für die Tantiemenausschüttung', en: 'Counting pixels that record text views for royalty distribution', fr: 'Pixels de comptage qui enregistrent les consultations de textes pour la répartition des redevances', es: 'Píxeles de conteo que registran las visualizaciones de textos para el reparto de regalías', it: 'Pixel di conteggio che registrano le visualizzazioni dei testi per la ripartizione dei diritti', nl: 'Telpixels die tekstweergaven registreren voor de verdeling van royalty\'s', pl: 'Piksele zliczające rejestrujące wyświetlenia tekstu na potrzeby podziału tantiem', da: 'Tællepixels, der registrerer tekstvisninger til fordeling af royalties', sv: 'Räknepixlar som registrerar textvisningar för fördelning av royalties' } },
        // Newsletter Dienste (Brevo/ActiveCampaign/Mailchimp/…): Titel, Anbieter
        // und Datenschutzlink hängen vom gewählten Anbieter ab und kommen daher
        // immer vollständig vom Server (siehe SettingsLocalizer::
        // build_active_services_config()/build_necessary_services_config()) -
        // kein statischer Fallback hier nötig.
        // Automatisch erkannte Script-Dienste: Kategorie kommt bereits vom Server,
        // hier nur Anbieter-/Datenschutz-Infos ergänzen.
        pixelhelper: { provider: 'Pixelhelper', privacy_url: 'https://pixelhelper.de/datenschutz/' },
        // Google Fonts: Name/Beschreibung stammen aus den title_xx/description_xx-
        // Feldern des "google_fonts"-Eintrags in cookie-database.json._script_patterns
        // - hier dieselben Texte, damit auch der Sprach-Umschalter (statt nur die
        // vom Server per Pixelmate_I18n::pick_localized() gewählte WP-Sprache)
        // greift.
        google_fonts: { name: { de: 'Google Fonts (extern geladen)', en: 'Google Fonts (loaded externally)', fr: 'Google Fonts (chargées en externe)', es: 'Google Fonts (cargadas externamente)', it: 'Google Fonts (caricati esternamente)', nl: 'Google Fonts (extern geladen)', pl: 'Google Fonts (ładowane zewnętrznie)', da: 'Google Fonts (indlæst eksternt)', sv: 'Google Fonts (laddas externt)' }, provider: 'Google LLC', privacy_url: 'https://policies.google.com/privacy', description: { de: 'Google Fonts, die direkt von externen Google-Servern (fonts.googleapis.com / fonts.gstatic.com) geladen werden.', en: "Google Fonts loaded directly from Google's external servers (fonts.googleapis.com / fonts.gstatic.com).", fr: 'Google Fonts chargées directement depuis les serveurs externes de Google (fonts.googleapis.com / fonts.gstatic.com).', es: 'Google Fonts que se cargan directamente desde servidores externos de Google (fonts.googleapis.com / fonts.gstatic.com).', it: 'Google Fonts caricati direttamente da server esterni di Google (fonts.googleapis.com / fonts.gstatic.com).', nl: 'Google Fonts die rechtstreeks van externe Google-servers (fonts.googleapis.com / fonts.gstatic.com) worden geladen.', pl: 'Czcionki Google ładowane bezpośrednio z zewnętrznych serwerów Google (fonts.googleapis.com / fonts.gstatic.com).', da: 'Google Fonts, der indlæses direkte fra Googles eksterne servere (fonts.googleapis.com / fonts.gstatic.com).', sv: 'Google Fonts som laddas direkt från Googles externa servrar (fonts.googleapis.com / fonts.gstatic.com).' } },
        // Comments Anywhere: Servicename kam bisher überhaupt nicht vom Server
        // (nur die Beschreibung, siehe SettingsLocalizer::build_active_services_config()) -
        // ohne Eintrag hier fiel die Anzeige auf die rohe Service-ID zurück.
        comments_anywhere: { name: 'Comments Anywhere', category: 'external', description: { de: 'Speichert Likes und Kommentare auf dem eigenen Server für ein persönliches Nutzererlebnis.', en: 'Stores likes and comments on our own server for a personalized user experience.', fr: "Enregistre les mentions J'aime et les commentaires sur notre propre serveur pour une expérience utilisateur personnalisée.", es: 'Guarda los "me gusta" y los comentarios en nuestro propio servidor para una experiencia de usuario personalizada.', it: 'Salva i "mi piace" e i commenti sul nostro server per un\'esperienza utente personalizzata.', nl: 'Slaat likes en reacties op onze eigen server op voor een persoonlijke gebruikerservaring.', pl: 'Zapisuje polubienia i komentarze na własnym serwerze w celu zapewnienia spersonalizowanego doświadczenia użytkownika.', da: 'Gemmer likes og kommentarer på vores egen server for en personlig brugeroplevelse.', sv: 'Sparar gilla-markeringar och kommentarer på vår egen server för en personlig användarupplevelse.' } },
        hotjar: { provider: 'Hotjar Ltd.', privacy_url: 'https://www.hotjar.com/legal/policies/privacy/' },
        intercom: { provider: 'Intercom Inc.', privacy_url: 'https://www.intercom.com/legal/privacy' },
        linkedin_insight: { provider: 'LinkedIn Ireland Unlimited Company', privacy_url: 'https://www.linkedin.com/legal/privacy-policy' },
        tiktok_pixel: { provider: 'TikTok Pte. Ltd.', privacy_url: 'https://www.tiktok.com/legal/privacy-policy' },
        crisp_chat: { provider: 'Crisp IM SAS', privacy_url: 'https://crisp.chat/en/privacy/' },
        hubspot: { provider: 'HubSpot Inc.', privacy_url: 'https://legal.hubspot.com/privacy-policy' },
        stripe: { provider: 'Stripe Inc.', privacy_url: 'https://stripe.com/privacy' },
        cloudflare_cdn: { provider: 'Cloudflare Inc.', privacy_url: 'https://www.cloudflare.com/privacypolicy/' },
        paypal: { provider: 'PayPal (Europe) S.à r.l. et Cie, S.C.A.', privacy_url: 'https://www.paypal.com/myaccount/privacy/privacyhub' },
        mollie: { provider: 'Mollie B.V.', privacy_url: 'https://www.mollie.com/gb/privacy' },
        woocommerce_payments: { provider: 'Automattic Inc. / Stripe, Inc.', privacy_url: 'https://automattic.com/privacy/' },
        apple_pay: { provider: 'Apple Distribution International Ltd.', privacy_url: 'https://www.apple.com/legal/privacy/' },
        google_pay: { provider: 'Google Ireland Limited', privacy_url: 'https://policies.google.com/privacy' },
        klarna: { provider: 'Klarna Bank AB (publ)', privacy_url: 'https://www.klarna.com/de/datenschutz/' },
        amazon_pay: { provider: 'Amazon Payments Europe s.c.a.', privacy_url: 'https://pay.amazon.de/help/201212490' },
        adyen: { provider: 'Adyen N.V.', privacy_url: 'https://www.adyen.com/policies-and-disclaimer/privacy-policy' },
        braintree: { provider: 'PayPal (Europe) S.à r.l. et Cie, S.C.A.', privacy_url: 'https://www.braintreepayments.com/de/legal/braintree-privacy-policy' },
        square: { provider: 'Block, Inc. (Square)', privacy_url: 'https://squareup.com/de/de/legal/general/privacy' },
        sumup: { provider: 'SumUp Limited', privacy_url: 'https://www.sumup.com/de-de/datenschutz/' },
        unzer: { provider: 'Unzer GmbH', privacy_url: 'https://www.unzer.com/de/datenschutz/' }
    };

    // Normalisiert eine beliebige Kategorie-/Consent-Bezeichnung (Admin-Dropdown,
    // Freitext-Consent-Typ oder cookie-database.json) auf eine der vier
    // tatsächlichen Consent-Gattungen, die das Banner kennt. Muss mit
    // DSGVO_Pixelmate_IntegrationSettings::normalize_consent_category() (PHP)
    // übereinstimmen.
    static CATEGORY_MAP = {
        necessary: 'necessary', notwendig: 'necessary', functional: 'necessary',
        funktional: 'necessary', essential: 'necessary',
        analytics: 'analytics', analyse: 'analytics', analytical: 'analytics',
        statistics: 'analytics', statistik: 'analytics', performance: 'analytics',
        marketing: 'marketing', werbung: 'marketing', advertising: 'marketing',
        advertisement: 'marketing', ads: 'marketing', targeting: 'marketing',
        external: 'external', extern: 'external', embedded: 'external',
        eingebettet: 'external', media: 'external', other: 'external',
        sonstiges: 'external', sonstige: 'external'
    };

    static normalizeCategory(raw, fallback = 'external') {
        const key = String(raw ?? '').trim().toLowerCase();
        if (key === '') return fallback;
        return SettingsModal.CATEGORY_MAP[key] || fallback;
    }

    // Alle Zahlungsanbieter-Vorlagen - muss mit
    // DSGVO_Pixelmate_IntegrationSettings::get_payment_service_keys() (PHP)
    // übereinstimmen. Dient hier nur dazu, für Anbieter OHNE eigene Cookie-
    // Einträge in der cookie-database.json (siehe serviceMapping in
    // getServiceCookieInfo()) einen ehrlichen, neutralen Hinweis statt eines
    // stillschweigend leeren Cookie-Blocks zu zeigen.
    static PAYMENT_SERVICE_IDS = new Set([
        'stripe', 'paypal', 'mollie', 'klarna', 'amazon_pay', 'adyen',
        'braintree', 'square', 'sumup', 'unzer', 'woocommerce_payments',
        'apple_pay', 'google_pay'
    ]);

    static MODAL_TEXTS = {
        tabSettings:         { de: 'Einstellungen',    en: 'Settings', fr: 'Paramètres', es: 'Configuración', it: 'Impostazioni', nl: 'Instellingen', pl: 'Ustawienia', da: 'Indstillinger', sv: 'Inställningar' },
        tabDataInfo:         { de: 'Datenauskunft',    en: 'Data Information', fr: 'Informations sur les données', es: 'Información de datos', it: 'Informazioni sui dati', nl: 'Gegevensinzage', pl: 'Informacja o danych', da: 'Dataoplysning', sv: 'Datainformation' },
        settingsTitle:       { de: 'Cookie-Einstellungen', en: 'Cookie Settings', fr: 'Paramètres des cookies', es: 'Configuración de cookies', it: 'Impostazioni dei cookie', nl: 'Cookie-instellingen', pl: 'Ustawienia plików cookie', da: 'Cookieindstillinger', sv: 'Cookie-inställningar' },
        settingsDescription: { de: 'Wir verwenden Cookies und ähnliche Technologien. Sie können unten für jeden Zweck einzeln zustimmen.', en: 'We use cookies and similar technologies. You can consent to each purpose individually below.', fr: 'Nous utilisons des cookies et des technologies similaires. Vous pouvez consentir séparément à chaque finalité ci-dessous.', es: 'Utilizamos cookies y tecnologías similares. Puede aceptar por separado cada finalidad a continuación.', it: 'Utilizziamo cookie e tecnologie simili. Può acconsentire di seguito separatamente per ogni finalità.', nl: 'Wij gebruiken cookies en vergelijkbare technologieën. Hieronder kunt u per doel afzonderlijk toestemming geven.', pl: 'Używamy plików cookie i podobnych technologii. Poniżej mogą Państwo wyrazić zgodę osobno dla każdego celu.', da: 'Vi bruger cookies og lignende teknologier. Du kan give samtykke til hvert formål separat nedenfor.', sv: 'Vi använder cookies och liknande tekniker. Du kan samtycka till varje ändamål separat nedan.' },
        saveSelection:       { de: 'Auswahl speichern', en: 'Save Selection', fr: 'Enregistrer la sélection', es: 'Guardar selección', it: 'Salva selezione', nl: 'Selectie opslaan', pl: 'Zapisz wybór', da: 'Gem valg', sv: 'Spara val' },
        acceptAll:           { de: 'Alle akzeptieren', en: 'Accept All', fr: 'Tout accepter', es: 'Aceptar todo', it: 'Accetta tutto', nl: 'Alles accepteren', pl: 'Zaakceptuj wszystkie', da: 'Accepter alle', sv: 'Acceptera alla' },
        rejectAll:           { de: 'Alle ablehnen',    en: 'Reject All', fr: 'Tout refuser', es: 'Rechazar todo', it: 'Rifiuta tutto', nl: 'Alles weigeren', pl: 'Odrzuć wszystkie', da: 'Afvis alle', sv: 'Avvisa alla' },
        closeButton:         { de: 'Schließen',        en: 'Close', fr: 'Fermer', es: 'Cerrar', it: 'Chiudi', nl: 'Sluiten', pl: 'Zamknij', da: 'Luk', sv: 'Stäng' },
        required:            { de: 'Erforderlich',     en: 'Required', fr: 'Obligatoire', es: 'Obligatorio', it: 'Necessario', nl: 'Verplicht', pl: 'Wymagane', da: 'Påkrævet', sv: 'Obligatorisk' },
        privacy:             { de: 'Datenschutz',      en: 'Privacy', fr: 'Confidentialité', es: 'Privacidad', it: 'Privacy', nl: 'Privacy', pl: 'Prywatność', da: 'Privatliv', sv: 'Integritet' },
        // Fußzeilen-Links (Datenschutz/Impressum) - dieselben Übersetzungen wie
        // PixelmateBanner.DEFAULT_TEXTS.privacyLink/imprintLink, hier separat
        // hinterlegt, da t() nicht auf das Banner-Textobjekt zugreifen kann.
        privacyLink:         { de: 'Datenschutz',      en: 'Privacy', fr: 'Confidentialité', es: 'Privacidad', it: 'Privacy', nl: 'Privacy', pl: 'Prywatność', da: 'Privatliv', sv: 'Integritet' },
        imprintLink:         { de: 'Impressum',        en: 'Imprint', fr: 'Mentions légales', es: 'Aviso legal', it: 'Note legali', nl: 'Colofon', pl: 'Nota prawna', da: 'Kolofon', sv: 'Om webbplatsen' },
        // Für Screenreader angehängter Hinweis an Links, die in einem neuen Tab
        // öffnen (target="_blank") - visuell versteckt via .pixelmate-sr-only.
        opensInNewTab:       { de: '(öffnet in neuem Tab)', en: '(opens in new tab)', fr: '(s\'ouvre dans un nouvel onglet)', es: '(se abre en una pestaña nueva)', it: '(si apre in una nuova scheda)', nl: '(opent in nieuw tabblad)', pl: '(otwiera się w nowej karcie)', da: '(åbner i en ny fane)', sv: '(öppnas i en ny flik)' },
        // Für die per aria-live-Region angesagte Status-Meldung nach "Auswahl
        // speichern" im Modal (WCAG 4.1.3) - siehe saveSelection() und
        // PixelmateBanner.js announceToScreenReader(). Für "Alle
        // akzeptieren"/"Alle ablehnen" im Modal wird stattdessen die
        // Banner-eigene Ansage verwendet, da diese Buttons auf
        // banner.acceptAll()/rejectAll() delegieren.
        consentSavedAnnouncement: { de: 'Cookie-Auswahl gespeichert.', en: 'Cookie selection saved.', fr: 'Sélection de cookies enregistrée.', es: 'Selección de cookies guardada.', it: 'Selezione dei cookie salvata.', nl: 'Cookie-selectie opgeslagen.', pl: 'Wybór plików cookie zapisany.', da: 'Cookie-valg gemt.', sv: 'Cookie-val har sparats.' },
        dataInfoTitle:       { de: 'Datenauskunft',    en: 'Data Information', fr: 'Informations sur les données', es: 'Información de datos', it: 'Informazioni sui dati', nl: 'Gegevensinzage', pl: 'Informacja o danych', da: 'Dataoplysning', sv: 'Datainformation' },
        dataInfoDesc:        { de: 'Hier sehen Sie die Einwilligungsnachweise, die sicher diesem Browser zugeordnet sind. Andere Geräte oder Browser werden nicht angezeigt.', en: 'Here you can see consent records securely linked to this browser. Other devices or browsers are not shown.', fr: 'Vous voyez ici les consentements associés en toute sécurité à ce navigateur. Les autres appareils ou navigateurs ne sont pas affichés.', es: 'Aquí puede ver los consentimientos vinculados de forma segura a este navegador. No se muestran otros dispositivos o navegadores.', it: 'Qui può vedere i consensi associati in modo sicuro a questo browser. Altri dispositivi o browser non vengono mostrati.', nl: 'Hier ziet u toestemmingsgegevens die veilig aan deze browser zijn gekoppeld. Andere apparaten of browsers worden niet getoond.', pl: 'Tutaj można zobaczyć zgody bezpiecznie powiązane z tą przeglądarką. Inne urządzenia i przeglądarki nie są wyświetlane.', da: 'Her kan du se samtykker, der er sikkert knyttet til denne browser. Andre enheder eller browsere vises ikke.', sv: 'Här visas samtycken som är säkert kopplade till den här webbläsaren. Andra enheter eller webbläsare visas inte.' },
        loading:             { de: 'Daten werden geladen…', en: 'Loading data…', fr: 'Chargement des données…', es: 'Cargando datos…', it: 'Caricamento dei dati…', nl: 'Gegevens worden geladen…', pl: 'Ładowanie danych…', da: 'Indlæser data…', sv: 'Laddar data…' },
        cookiesUsed:         { de: 'Verwendete Cookies:', en: 'Cookies used:', fr: 'Cookies utilisés :', es: 'Cookies utilizadas:', it: 'Cookie utilizzati:', nl: 'Gebruikte cookies:', pl: 'Wykorzystywane pliki cookie:', da: 'Anvendte cookies:', sv: 'Använda cookies:' },
        paymentCookieFallback: {
            de: 'Für diesen Zahlungsanbieter liegen uns keine eigenen, geprüften Cookie-Namen vor. Der Anbieter kann bei der Zahlungsabwicklung eigene Cookies auf seiner eigenen Domain setzen – Details dazu findest du in der Datenschutzerklärung des Anbieters.',
            en: 'We don’t have verified cookie names of our own for this payment provider. The provider may set its own cookies on its own domain while processing the payment – see the provider’s privacy policy for details.',
            fr: "Nous ne disposons pas de noms de cookies vérifiés pour ce prestataire de paiement. Celui-ci peut définir ses propres cookies sur son propre domaine lors du traitement du paiement – consultez la politique de confidentialité du prestataire pour plus de détails.",
            es: 'No disponemos de nombres de cookies propios y verificados para este proveedor de pago. El proveedor puede establecer sus propias cookies en su propio dominio durante el procesamiento del pago; consulte la política de privacidad del proveedor para más detalles.',
            it: 'Per questo fornitore di pagamento non disponiamo di nomi di cookie propri e verificati. Il fornitore può impostare propri cookie sul proprio dominio durante l\'elaborazione del pagamento – i dettagli sono disponibili nell\'informativa sulla privacy del fornitore.',
            nl: 'Voor deze betalingsaanbieder beschikken wij niet over eigen, geverifieerde cookienamen. De aanbieder kan bij de betalingsverwerking eigen cookies op zijn eigen domein plaatsen – details hierover vindt u in het privacybeleid van de aanbieder.',
            pl: 'Nie dysponujemy własnymi, zweryfikowanymi nazwami plików cookie dla tego dostawcy płatności. Dostawca może podczas przetwarzania płatności ustawiać własne pliki cookie na swojej własnej domenie – szczegóły można znaleźć w polityce prywatności dostawcy.',
            da: 'Vi har ikke egne, verificerede cookienavne for denne betalingsudbyder. Udbyderen kan under betalingsbehandlingen sætte sine egne cookies på sit eget domæne – se udbyderens privatlivspolitik for flere detaljer.',
            sv: 'Vi har inga egna, verifierade cookienamn för denna betalningsleverantör. Leverantören kan sätta egna cookies på sin egen domän vid betalningshanteringen – se leverantörens integritetspolicy för mer information.'
        },
        purpose:             { de: 'Zweck:',           en: 'Purpose:', fr: 'Finalité :', es: 'Finalidad:', it: 'Scopo:', nl: 'Doel:', pl: 'Cel:', da: 'Formål:', sv: 'Syfte:' },
        allCookiesAccepted:  { de: 'Alle Cookies akzeptiert', en: 'All cookies accepted', fr: 'Tous les cookies acceptés', es: 'Todas las cookies aceptadas', it: 'Tutti i cookie accettati', nl: 'Alle cookies geaccepteerd', pl: 'Wszystkie pliki cookie zaakceptowane', da: 'Alle cookies accepteret', sv: 'Alla cookies accepterade' },
        consentServices:     { de: 'Zugestimmte Dienste', en: 'Consented services', fr: 'Services acceptés', es: 'Servicios aceptados', it: 'Servizi approvati', nl: 'Geaccepteerde diensten', pl: 'Zaakceptowane usługi', da: 'Godkendte tjenester', sv: 'Godkända tjänster' },
        noEntries:           { de: 'Keine gespeicherten Einträge für diesen Browser.', en: 'No stored entries for this browser.', fr: 'Aucune entrée enregistrée pour ce navigateur.', es: 'No hay entradas guardadas para este navegador.', it: 'Nessuna voce salvata per questo browser.', nl: 'Geen opgeslagen gegevens voor deze browser.', pl: 'Brak zapisanych wpisów dla tej przeglądarki.', da: 'Ingen gemte poster for denne browser.', sv: 'Inga sparade poster för den här webbläsaren.' },
        storedEntries:       { de: 'Gespeicherte Einträge', en: 'Stored entries', fr: 'Entrées enregistrées', es: 'Entradas guardadas', it: 'Voci salvate', nl: 'Opgeslagen gegevens', pl: 'Zapisane wpisy', da: 'Gemte poster', sv: 'Sparade poster' },
        errorLoading:        { de: 'Fehler beim Laden der Daten.', en: 'Error loading data.', fr: 'Erreur lors du chargement des données.', es: 'Error al cargar los datos.', it: 'Errore durante il caricamento dei dati.', nl: 'Fout bij het laden van de gegevens.', pl: 'Błąd podczas ładowania danych.', da: 'Fejl ved indlæsning af data.', sv: 'Fel vid inläsning av data.' },
        configUnavailable:   { de: 'Konfiguration nicht verfügbar.', en: 'Configuration not available.', fr: 'Configuration non disponible.', es: 'Configuración no disponible.', it: 'Configurazione non disponibile.', nl: 'Configuratie niet beschikbaar.', pl: 'Konfiguracja niedostępna.', da: 'Konfiguration ikke tilgængelig.', sv: 'Konfigurationen är inte tillgänglig.' },
        errorGeneric:        { de: 'Fehler:',          en: 'Error:', fr: 'Erreur :', es: 'Error:', it: 'Errore:', nl: 'Fout:', pl: 'Błąd:', da: 'Fejl:', sv: 'Fel:' },
        categoryInfoLabel:   { de: 'Kategorie:',       en: 'Category:', fr: 'Catégorie :', es: 'Categoría:', it: 'Categoria:', nl: 'Categorie:', pl: 'Kategoria:', da: 'Kategori:', sv: 'Kategori:' },
        categories: {
            necessary: {
                name:        { de: 'Notwendig',        en: 'Necessary', fr: 'Nécessaire', es: 'Necesario', it: 'Necessari', nl: 'Noodzakelijk', pl: 'Niezbędne', da: 'Nødvendige', sv: 'Nödvändiga' },
                description: { de: 'Diese Cookies sind für die Grundfunktionen der Website erforderlich.', en: 'These cookies are required for the basic functions of the website.', fr: 'Ces cookies sont nécessaires aux fonctions de base du site.', es: 'Estas cookies son necesarias para las funciones básicas del sitio web.', it: 'Questi cookie sono necessari per le funzioni di base del sito web.', nl: 'Deze cookies zijn noodzakelijk voor de basisfuncties van de website.', pl: 'Te pliki cookie są niezbędne do podstawowych funkcji witryny.', da: 'Disse cookies er nødvendige for webstedets grundlæggende funktioner.', sv: 'Dessa cookies är nödvändiga för webbplatsens grundläggande funktioner.' }
            },
            analytics: {
                name:        { de: 'Analyse',          en: 'Analytics', fr: 'Analyse', es: 'Estadísticas', it: 'Statistiche', nl: 'Statistieken', pl: 'Statystyka', da: 'Statistik', sv: 'Statistik' },
                description: { de: 'Diese Cookies helfen uns, die Nutzung der Website zu verstehen.', en: 'These cookies help us understand how the website is used.', fr: "Ces cookies nous aident à comprendre l'utilisation du site.", es: 'Estas cookies nos ayudan a comprender el uso del sitio web.', it: 'Questi cookie ci aiutano a comprendere l\'utilizzo del sito web.', nl: 'Deze cookies helpen ons te begrijpen hoe de website wordt gebruikt.', pl: 'Te pliki cookie pomagają nam zrozumieć sposób korzystania z witryny.', da: 'Disse cookies hjælper os med at forstå, hvordan webstedet bruges.', sv: 'Dessa cookies hjälper oss att förstå hur webbplatsen används.' }
            },
            marketing: {
                name:        { de: 'Marketing',        en: 'Marketing', fr: 'Marketing', es: 'Marketing', it: 'Marketing', nl: 'Marketing', pl: 'Marketing', da: 'Marketing', sv: 'Marknadsföring' },
                description: { de: 'Diese Cookies werden für Werbung und Remarketing verwendet.', en: 'These cookies are used for advertising and remarketing.', fr: 'Ces cookies sont utilisés pour la publicité et le remarketing.', es: 'Estas cookies se utilizan para publicidad y remarketing.', it: 'Questi cookie vengono utilizzati per pubblicità e remarketing.', nl: 'Deze cookies worden gebruikt voor reclame en remarketing.', pl: 'Te pliki cookie są wykorzystywane do reklamy i remarketingu.', da: 'Disse cookies bruges til reklame og remarketing.', sv: 'Dessa cookies används för annonsering och remarketing.' }
            },
            external: {
                name:        { de: 'Externe Inhalte',  en: 'External Content', fr: 'Contenus externes', es: 'Contenido externo', it: 'Contenuti esterni', nl: 'Externe inhoud', pl: 'Treści zewnętrzne', da: 'Eksternt indhold', sv: 'Externt innehåll' },
                description: { de: 'Diese Cookies erlauben das Laden externer Inhalte wie Videos und Karten.', en: 'These cookies allow loading external content such as videos and maps.', fr: 'Ces cookies permettent de charger du contenu externe tel que des vidéos et des cartes.', es: 'Estas cookies permiten cargar contenido externo, como vídeos y mapas.', it: 'Questi cookie consentono di caricare contenuti esterni come video e mappe.', nl: 'Deze cookies maken het laden van externe inhoud zoals video\'s en kaarten mogelijk.', pl: 'Te pliki cookie umożliwiają ładowanie treści zewnętrznych, takich jak filmy i mapy.', da: 'Disse cookies gør det muligt at indlæse eksternt indhold som videoer og kort.', sv: 'Dessa cookies möjliggör laddning av externt innehåll som videor och kartor.' }
            }
        }
    };

    constructor(banner) {
        this.banner = banner;
        this.config = banner.config;
        this.design = banner.design;
        this.texts = banner.texts;

        this.modalElement = null;
        this.overlayElement = null;
        this.isOpen = false;
        // Element, das das Modal geöffnet hat - der Fokus kehrt beim Schließen dorthin zurück
        this.previouslyFocusedElement = null;
        this.keydownHandler = null;
        this.activeTab = 'settings'; // 'settings' oder 'cookies'
        this.cookieDatabase = null;

        // Sprache: gespeicherte Auswahl hat Vorrang, dann WP-Locale
        const wpLang = this.config.settings?.language || '';
        const savedLang = localStorage.getItem('pixelmate_lang');
        this.currentLang = savedLang || (wpLang.startsWith('de') ? 'de' : wpLang.startsWith('fr') ? 'fr' : wpLang.startsWith('es') ? 'es' : wpLang.startsWith('it') ? 'it' : wpLang.startsWith('nl') ? 'nl' : wpLang.startsWith('pl') ? 'pl' : wpLang.startsWith('da') ? 'da' : wpLang.startsWith('sv') ? 'sv' : 'en');

        // Verfügbare Kategorien (strukturell; Texte werden über t() gerendert)
        this.categories = {
            necessary: { required: true,  services: [] },
            analytics: { required: false, services: [] },
            marketing: { required: false, services: [] },
            external:  { required: false, services: [] }
        };

        // Services aus Config laden
        this.loadServices();

        // Cookie-Datenbank laden – Promise speichern, damit open() darauf warten kann
        this.cookieDatabaseReady = this.loadCookieDatabase();
    }

    /**
     * Übersetzungshelfer: gibt den Text für den aktuellen currentLang zurück.
     * Unterstützt Punkt-Notation für verschachtelte Schlüssel (z.B. 'categories.necessary.name').
     */
    t(key) {
        const parts = key.split('.');
        let obj = SettingsModal.MODAL_TEXTS;
        for (const p of parts) obj = obj?.[p];
        return obj?.[this.currentLang] ?? obj?.de ?? key;
    }

    /**
     * Text-Helfer für Elemente, die sowohl vom Banner (this.texts, z.B. per
     * Admin-Einstellung individuell angepasst) als auch von MODAL_TEXTS/t()
     * (reagiert auf den Sprach-Umschalter im Modal) einen Wert liefern können.
     * this.texts spiegelt die Banner-Sprache beim Öffnen des Modals wider,
     * NICHT den hier per Sprach-Umschalter gewählten currentLang - ohne diese
     * Priorisierung blieben Elemente wie "Alle ablehnen" oder "Datenschutz"/
     * "Impressum" beim Umschalten immer in der Banner-Sprache stehen (siehe
     * gleiches Muster bei localizedDesc() in loadServices()).
     */
    localizedBannerText(bannerKey, tKey) {
        return this.currentLang === 'de'
            ? (this.texts?.[bannerKey] || this.t(tKey))
            : (this.t(tKey) || this.texts?.[bannerKey]);
    }

    /**
     * Cookie-Datenbank laden. Nutzt die serverseitig auf die aktiven Sprachen
     * gefilterte Datei (siehe DSGVO_Pixelmate_CookieDatabase::get_filtered_database_url(),
     * localized als pixelmate_vars.cookie_database_url) - fällt auf die volle
     * Quelldatei zurück, falls dieser Wert fehlt (z. B. ältere Cache-Layer,
     * die pixelmate_vars noch ohne dieses Feld ausliefern).
     */
    async loadCookieDatabase() {
        const url = pixelmate_vars.cookie_database_url
            || (pixelmate_vars.plugin_url + '/include/data/cookie-database.json');
        try {
            const response = await fetch(url);
            this.cookieDatabase = await response.json();
        } catch (error) {
            pmError('Failed to load cookie database:', error);
            this.cookieDatabase = {};
        }
    }

    /**
     * Beschreibungstext eines Cookies sprachabhängig auswählen
     */
    getCookieDescription(cookie) {
        if (this.currentLang === 'de' && cookie.description_de) return cookie.description_de;
        if (this.currentLang === 'fr' && cookie.description_fr) return cookie.description_fr;
        if (this.currentLang === 'es' && cookie.description_es) return cookie.description_es;
        if (this.currentLang === 'it' && cookie.description_it) return cookie.description_it;
        if (this.currentLang === 'nl' && cookie.description_nl) return cookie.description_nl;
        if (this.currentLang === 'pl' && cookie.description_pl) return cookie.description_pl;
        if (this.currentLang === 'da' && cookie.description_da) return cookie.description_da;
        if (this.currentLang === 'sv' && cookie.description_sv) return cookie.description_sv;
        return cookie.description;
    }

    /**
     * Sprache umschalten und Modal neu aufbauen
     */
    switchLanguage(lang) {
        if (lang === this.currentLang) return;
        this.currentLang = lang;
        if (this.modalElement) {
            this.modalElement.setAttribute('lang', lang);
        }
        localStorage.setItem('pixelmate_lang', lang);
        // Dienste-Beschreibungen hängen von currentLang ab (siehe loadServices())
        // und wurden nur einmal im Constructor geladen - ohne Neuladen blieben
        // sie beim Sprachwechsel auf der zuerst geladenen Sprache stehen.
        this.loadServices();
        this.rebuildModal();
    }

    /**
     * Modal-Inhalt neu rendern (erhält den Tab- und Consent-Zustand)
     */
    rebuildModal() {
        if (!this.modalElement) return;
        const activeTab = this.activeTab;
        this.modalElement.innerHTML = this.getModalHTML();
        this.attachModalListeners();
        this.loadCurrentConsent();
        if (activeTab !== 'settings') this.switchTab(activeTab);
    }

    /**
     * Lade aktive Services aus der Config
     */
    loadServices() {
        // Wiederholt aufrufbar (z.B. bei switchLanguage()) - Service-Listen vor
        // dem Neubefüllen leeren, sonst würden bei jedem Aufruf Duplikate
        // angehängt (loadServices() pusht unten in this.categories.*.services).
        Object.values(this.categories).forEach(cat => { cat.services = []; });

        const services = this.config.activeServices || {};

        const desc = (id) => SettingsModal.SERVICE_DESCRIPTIONS[id]?.[this.currentLang]
            || SettingsModal.SERVICE_DESCRIPTIONS[id]?.de
            || '';

        // Server-seitige Beschreibungen (services.xxx.description) kommen aus
        // PHP-__()-Aufrufen und folgen der WordPress-/WPML-/Polylang-Sprache der
        // Seite - NICHT der hier im Modal per Sprach-Umschalter gewählten
        // currentLang. Ohne WPML/Polylang ist der PHP-Text praktisch immer
        // Deutsch (siehe Pixelmate_I18n::current_language()), weshalb die
        // Cookie-/Dienste-Infos sonst unabhängig von der gewählten Sprache immer
        // auf Deutsch blieben. Für die mitgelieferten Standarddienste (mit
        // eigenem mehrsprachigem Eintrag in SERVICE_DESCRIPTIONS) hat deshalb bei
        // einer von Deutsch abweichenden currentLang die JS-seitige Übersetzung
        // Vorrang; der Server-Text bleibt Fallback, falls keine Übersetzung
        // vorliegt. Für currentLang 'de' bleibt der Server-Text (der ggf. vom
        // Admin im Backend individuell angepasst wurde) weiterhin führend.
        const localizedDesc = (id, serverDescription) => {
            if (this.currentLang !== 'de') {
                return desc(id) || serverDescription || '';
            }
            return serverDescription || desc(id);
        };

        // Google Analytics
        if (services.google_analytics) {
            this.categories.analytics.services.push({
                id: 'google_analytics',
                name: 'Google Analytics',
                description: localizedDesc('google_analytics', services.google_analytics.description),
                provider: 'Google LLC',
                privacy_url: 'https://policies.google.com/privacy'
            });
        }

        // Facebook Pixel
        if (services.facebook_pixel) {
            this.categories.marketing.services.push({
                id: 'facebook_pixel',
                name: 'Facebook Pixel',
                description: localizedDesc('facebook_pixel', services.facebook_pixel.description),
                provider: 'Meta Platforms Inc.',
                privacy_url: 'https://www.facebook.com/privacy/policy/'
            });
        }

        // Matomo
        if (services.matomo) {
            this.categories.analytics.services.push({
                id: 'matomo',
                name: 'Matomo',
                description: localizedDesc('matomo', services.matomo.description),
                provider: 'Matomo',
                privacy_url: this.config.urls.privacy || '#'
            });
        }

        // YouTube
        if (services.youtube) {
            this.categories.external.services.push({
                id: 'youtube',
                name: 'YouTube',
                description: localizedDesc('youtube', services.youtube.description),
                provider: 'Google LLC',
                privacy_url: 'https://policies.google.com/privacy'
            });
        }

        // Vimeo
        if (services.vimeo) {
            this.categories.external.services.push({
                id: 'vimeo',
                name: 'Vimeo',
                description: localizedDesc('vimeo', services.vimeo.description),
                provider: 'Vimeo, Inc.',
                privacy_url: 'https://vimeo.com/privacy'
            });
        }

        // Dailymotion
        if (services.dailymotion) {
            this.categories.external.services.push({
                id: 'dailymotion',
                name: 'Dailymotion',
                description: localizedDesc('dailymotion', services.dailymotion.description),
                provider: 'Dailymotion SA',
                privacy_url: 'https://legal.dailymotion.com/en/privacy-policy/'
            });
        }

        // Spotify
        if (services.spotify) {
            this.categories.external.services.push({
                id: 'spotify',
                name: 'Spotify',
                description: localizedDesc('spotify', services.spotify.description),
                provider: 'Spotify AB',
                privacy_url: 'https://www.spotify.com/privacy'
            });
        }

        // Google Maps
        if (services.google_maps) {
            this.categories.external.services.push({
                id: 'google_maps',
                name: 'Google Maps',
                description: localizedDesc('google_maps', services.google_maps.description),
                provider: 'Google LLC',
                privacy_url: 'https://policies.google.com/privacy'
            });
        }

        // WordPress.com Statistiken
        if (services.wordpress_stats) {
            this.categories.analytics.services.push({
                id: 'wordpress_stats',
                name: 'WordPress.com Statistiken',
                description: localizedDesc('wordpress_stats', services.wordpress_stats.description),
                provider: 'Automattic Inc.',
                privacy_url: 'https://automattic.com/privacy/'
            });
        }

        // Gravatar
        if (services.gravatar) {
            this.categories.external.services.push({
                id: 'gravatar',
                name: 'Gravatar',
                description: localizedDesc('gravatar', services.gravatar.description),
                provider: 'Automattic Inc.',
                privacy_url: 'https://automattic.com/privacy/'
            });
        }

        // SoundCloud
        if (services.soundcloud) {
            this.categories.external.services.push({
                id: 'soundcloud',
                name: 'SoundCloud',
                description: localizedDesc('soundcloud', services.soundcloud.description),
                provider: 'SoundCloud Global Limited & Co. KG',
                privacy_url: 'https://soundcloud.com/pages/privacy'
            });
        }

        // Notwendige Dienste (automatisch erkannt: WordPress-Kern, WooCommerce,
        // MemberPress, LearnDash, ... siehe SettingsLocalizer::build_necessary_services_config)
        const necessaryServices = services.necessary || {};
        Object.keys(necessaryServices).forEach(serviceId => {
            const svc = necessaryServices[serviceId];
            const i18n = SettingsModal.NECESSARY_SERVICE_I18N[serviceId];

            // Gleiche Priorität wie localizedDesc() oben: für currentLang 'de'
            // bleibt der (ggf. vom Admin individuell angepasste) Server-Text
            // führend, für jede andere Sprache hat die hier hinterlegte
            // Übersetzung Vorrang vor dem sonst immer deutschen Server-Text.
            const title = i18n
                ? (this.currentLang !== 'de'
                    ? (i18n.title?.[this.currentLang] || i18n.title?.de || svc.title || serviceId)
                    : (svc.title || i18n.title?.de || serviceId))
                : (svc.title || serviceId);

            const description = i18n
                ? (this.currentLang !== 'de'
                    ? (i18n.description?.[this.currentLang] || i18n.description?.de || svc.description || '')
                    : (svc.description || i18n.description?.de || ''))
                : (svc.description || '');

            const cookies = (svc.cookies || []).map(cookie => {
                const cookieI18n = i18n?.cookies?.[cookie.cookie];
                if (!cookieI18n) return cookie;

                const cookieDescription = this.currentLang !== 'de'
                    ? (cookieI18n.description?.[this.currentLang] || cookieI18n.description?.de || cookie.description || '')
                    : (cookie.description || cookieI18n.description?.de || '');

                let retentionPeriod = cookie.retentionPeriod || '';
                if (cookieI18n.retentionTemplate) {
                    // Dynamische Tagesangabe (z.B. pixelmate_consent): Zahl aus der
                    // Server-Antwort ("365 Tage") extrahieren und in die Vorlage
                    // der gewählten Sprache ("%d days") einsetzen.
                    const days = (retentionPeriod.match(/\d+/) || [])[0];
                    const template = this.currentLang !== 'de'
                        ? (cookieI18n.retentionTemplate[this.currentLang] || cookieI18n.retentionTemplate.de)
                        : cookieI18n.retentionTemplate.de;
                    if (days && template) retentionPeriod = template.replace('%d', days);
                } else if (cookieI18n.retentionPeriod) {
                    retentionPeriod = this.currentLang !== 'de'
                        ? (cookieI18n.retentionPeriod[this.currentLang] || cookieI18n.retentionPeriod.de || retentionPeriod)
                        : (retentionPeriod || cookieI18n.retentionPeriod.de);
                }

                return { ...cookie, description: cookieDescription, retentionPeriod };
            });

            this.categories.necessary.services.push({
                id: serviceId,
                name: title,
                description,
                provider: svc.provider || '',
                privacy_url: svc.privacy_url || '',
                cookies
            });
        });

        // Alle übrigen im Integrationsmanager aktivierten Dienste automatisch
        // einsortieren (z.B. Google Tag Manager, Pinterest, Twitter/X, Instagram,
        // Threads, Podigee, Deezer, Acast, Calendly, OpenStreetMap sowie alle
        // automatisch erkannten Script-Dienste wie Hotjar, Stripe, HubSpot, ...).
        // Bereits oben einzeln behandelte Dienste werden dabei übersprungen.
        const alreadyHandled = new Set([
            'necessary', 'google_analytics', 'facebook_pixel', 'matomo',
            'youtube', 'vimeo', 'dailymotion', 'spotify', 'google_maps', 'soundcloud',
            'wordpress_stats', 'gravatar'
        ]);
        Object.keys(services).forEach(serviceId => {
            if (alreadyHandled.has(serviceId)) return;

            const svcConfig = services[serviceId];
            if (!svcConfig || typeof svcConfig !== 'object') return;

            const info = SettingsModal.EXTRA_SERVICE_INFO[serviceId] || {};
            const rawCategory = svcConfig.category || info.category;
            // Robuste Normalisierung auf eine der vier Consent-Kategorien, damit
            // KEIN aktivierter Dienst durchs Raster fällt (Admin-Vokabular wie
            // 'embedded'/'other'/'functional' oder cookie-database-Kategorien
            // werden zuverlässig zugeordnet, Unbekanntes landet unter 'external').
            const category = SettingsModal.normalizeCategory(rawCategory);

            if (!this.categories[category]) return;

            // Gleiche Priorität wie localizedDesc() oben: bei abweichender
            // currentLang hat die mehrsprachige EXTRA_SERVICE_INFO-Beschreibung
            // Vorrang vor dem (praktisch immer deutschen) Server-Text.
            const infoDesc = info.description?.[this.currentLang] || info.description?.de || '';
            const description = this.currentLang !== 'de'
                ? (infoDesc || svcConfig.description || '')
                : (svcConfig.description || infoDesc);

            // info.name ist entweder ein fester Eigenname (String, z. B. "Pinterest")
            // oder - für Dienste mit sprachabhängigem Namen wie "google_fonts" - ein
            // Sprach-Objekt wie description; dieselbe Prioritätslogik anwenden.
            const infoName = typeof info.name === 'object'
                ? (info.name?.[this.currentLang] || info.name?.de || '')
                : (info.name || '');
            const name = typeof info.name === 'object'
                ? (this.currentLang !== 'de' ? (infoName || svcConfig.title || serviceId) : (svcConfig.title || infoName || serviceId))
                : (svcConfig.title || infoName || serviceId);

            this.categories[category].services.push({
                id: serviceId,
                name,
                description,
                provider: svcConfig.provider || info.provider || '',
                privacy_url: svcConfig.privacy_url || info.privacy_url || '',
                cookies: svcConfig.cookies || []
            });
        });
    }

    /**
     * Modal öffnen
     */
    async open() {
        if (this.isOpen) return;

        // Sicherstellen, dass die Cookie-DB geladen ist, bevor das Modal gerendert wird
        await this.cookieDatabaseReady;

        // Modal erstellen falls nicht vorhanden
        if (!this.modalElement) {
            this.createModal();
        }

        // Aktuelle Consent-Status laden
        this.loadCurrentConsent();

        // Element merken, das das Modal geöffnet hat, um den Fokus später
        // dorthin zurückzugeben (WCAG 2.4.3).
        this.previouslyFocusedElement = document.activeElement;

        // Anzeigen
        this.modalElement.style.display = 'flex';
        this.overlayElement.style.display = 'block';

        // Animation
        requestAnimationFrame(() => {
            this.modalElement.style.opacity = '1';
            this.modalElement.style.transform = 'translate(-50%, -50%) scale(1)';
            this.overlayElement.style.opacity = '1';
        });

        this.isOpen = true;

        // Body Scroll blockieren
        document.body.style.overflow = 'hidden';

        // Anders als der Banner ist das Modal tatsächlich modal (Seiten-Scroll
        // gesperrt, Overlay fängt Klicks ab). Deshalb gehört hier ein echter
        // Fokus-Fang dazu, sonst tabbt man aus einem scheinbar gesperrten Dialog
        // heraus in die dahinterliegende, nicht bedienbare Seite.
        this.modalElement.setAttribute('tabindex', '-1');
        try {
            this.modalElement.focus({ preventScroll: true });
        } catch (e) {
            this.modalElement.focus();
        }
    }

    /**
     * Liefert alle fokussierbaren Elemente im Modal in Dokumentreihenfolge.
     */
    getFocusableElements() {
        if (!this.modalElement) return [];

        const selector = [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])'
        ].join(', ');

        return Array.from(this.modalElement.querySelectorAll(selector))
            // offsetParent === null filtert ausgeblendete Elemente (z. B. der
            // gerade nicht aktive Tab) heraus. Die Schalter-Checkboxen haben
            // zwar 0x0 px, aber einen offsetParent — sie bleiben also drin.
            .filter(el => el.offsetParent !== null || el.type === 'checkbox');
    }

    /**
     * Tastaturbedienung des Modals: ESC schließt, Tab bleibt im Dialog gefangen.
     */
    handleModalKeydown(e) {
        if (!this.isOpen) return;

        if (e.key === 'Escape' || e.key === 'Esc') {
            e.preventDefault();
            // Schließen ohne Auswahl ändert die Einwilligung nicht — der bisherige
            // Stand bleibt unverändert bestehen.
            this.close();
            return;
        }

        if (e.key !== 'Tab') return;

        const focusable = this.getFocusableElements();
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;

        if (e.shiftKey && (active === first || active === this.modalElement)) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
        }
    }

    /**
     * Modal schließen
     */
    close() {
        if (!this.isOpen) return;

        // Animation raus
        this.modalElement.style.opacity = '0';
        this.modalElement.style.transform = 'translate(-50%, -50%) scale(0.95)';
        this.overlayElement.style.opacity = '0';

        setTimeout(() => {
            this.modalElement.style.display = 'none';
            this.overlayElement.style.display = 'none';
        }, this._transitionDurationMs || 300);

        this.isOpen = false;

        // Body Scroll wieder erlauben
        document.body.style.overflow = '';

        // Fokus dorthin zurückgeben, wo er vor dem Öffnen war (Widerruf-Button,
        // Banner-Link, Shortcode-Button ...). Sonst landet er nach dem Schließen
        // wieder am Seitenanfang.
        const target = this.previouslyFocusedElement;
        this.previouslyFocusedElement = null;

        if (target && typeof target.focus === 'function' && target.isConnected) {
            try {
                target.focus({ preventScroll: true });
            } catch (e) {
                target.focus();
            }
        }
    }

    /**
     * Modal erstellen
     */
    createModal() {
        // Bei aktivierter Systemeinstellung "Bewegungen reduzieren" (WCAG 2.3.3)
        // Ein-/Ausblenden praktisch instantan statt über 300ms Transition -
        // wird unten für Overlay, Modal und den Timeout in close() verwendet.
        this._transitionDurationMs = prefersReducedMotion() ? 1 : 300;
        this._transitionDuration = `${this._transitionDurationMs}ms`;

        // Overlay
        this.overlayElement = document.createElement('div');
        this.overlayElement.className = 'pixelmate-modal-overlay';
        this.overlayElement.style.cssText = `
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            height: 100% !important;
            background: rgba(0, 0, 0, 0.7) !important;
            z-index: 1000000 !important;
            opacity: 0 !important;
            transition: opacity ${this._transitionDuration} ease !important;
            display: none !important;
        `;

        // Modal
        this.modalElement = document.createElement('div');
        // "settings-banner-wrapper" ist die Klasse der Legacy-Plugin-Version.
        // Sie bleibt als Alias erhalten, damit Custom-CSS, das Kunden vor der
        // Migration z.B. für theme-bedingte Button-Anpassungen eingefügt
        // haben, nach dem Update weiterhin greift.
        this.modalElement.className = 'pixelmate-settings-modal settings-banner-wrapper';
        this.modalElement.setAttribute('role', 'dialog');
        this.modalElement.setAttribute('aria-modal', 'true');
        this.modalElement.setAttribute('aria-labelledby', 'pixelmate-modal-title');
        // lang-Attribut (WCAG 3.1.1/3.1.2), siehe gleichlautender Kommentar in
        // PixelmateBanner.js createBanner() - wird in switchLanguage() aktualisiert.
        this.modalElement.setAttribute('lang', this.currentLang);
        this.modalElement.style.cssText = `
            position: fixed !important;
            top: 50% !important;
            left: 50% !important;
            transform: translate(-50%, -50%) scale(0.95) !important;
            width: 90% !important;
            max-width: 800px !important;
            max-height: 85vh !important;
            background: ${this.design.colors?.background || '#ffffff'} !important;
            color: ${this.design.colors?.text || '#333333'} !important;
            border-radius: ${this.design.border?.radius || '8px'} !important;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3) !important;
            z-index: 1000001 !important;
            opacity: 0 !important;
            transition: opacity ${this._transitionDuration} ease, transform ${this._transitionDuration} ease !important;
            display: none !important;
            overflow: hidden !important;
            flex-direction: column !important;
            font-family: inherit !important;
        `;

        this.modalElement.innerHTML = this.getModalHTML();

        // Zum DOM hinzufügen
        document.body.appendChild(this.overlayElement);
        document.body.appendChild(this.modalElement);

        // Sichtbare Fokus-Markierung (die Schalter im Modal sind versteckte
        // Checkboxen und ohne eigene Regel nicht als fokussiert erkennbar).
        this.banner.injectA11yStyles();

        // ESC und Tab-Fang gelten für das gesamte Dokument, solange das Modal offen
        // ist — der Fokus kann beim Öffnen noch außerhalb liegen.
        this.keydownHandler = (e) => this.handleModalKeydown(e);
        document.addEventListener('keydown', this.keydownHandler);

        // Tastaturbedienung der Aufklapp-Pfeile, delegiert am Modal-Container.
        //
        // Die Kopfzeilen selbst sind bewusst NICHT fokussierbar: Sie enthalten
        // teilweise einen Link zur Datenschutzerklärung des Anbieters, und ein Link
        // innerhalb eines Bedienelements ist weder gültiges ARIA noch sinnvoll
        // bedienbar. Fokussierbar ist deshalb der Pfeil - die Kopfzeile bleibt für
        // die Maus wie bisher anklickbar.
        //
        // Bewusst hier und nicht in attachModalListeners(): rebuildModal() ruft jene
        // Methode nach jedem Sprachwechsel erneut auf, während modalElement bestehen
        // bleibt - ein dort registrierter Listener würde sich stapeln und den
        // Aufklapp-Zustand pro Tastendruck mehrfach umschalten.
        this.modalElement.addEventListener('keydown', (e) => {
            if (e.key !== ' ' && e.key !== 'Spacebar' && e.key !== 'Enter') {
                return;
            }

            const serviceIcon = e.target.closest('.pixelmate-service-expand-icon');
            if (serviceIcon) {
                e.preventDefault();
                e.stopPropagation();
                this.toggleServiceDetails(serviceIcon.dataset.serviceId);
                return;
            }

            const categoryIcon = e.target.closest('.pixelmate-category-expand-icon');
            if (categoryIcon) {
                e.preventDefault();
                e.stopPropagation();
                this.toggleCategoryServices(categoryIcon.dataset.categoryId);
            }
        });

        // Sprach-Umschalter (siehe getModalLanguageSwitcherHTML()): Globus-Icon-
        // Toggle öffnet/schließt das Dropdown - identisches Verhalten zu
        // PixelmateBanner::attachBannerEvents() (Language Switcher-Abschnitt).
        // Bewusst per Delegation hier und nicht in attachModalListeners() (siehe
        // Kommentar oben zu den Aufklapp-Pfeilen): rebuildModal() ersetzt
        // modalElement.innerHTML bei jedem Sprachwechsel, ein dort direkt an
        // .pixelmate-lang-toggle/.pixelmate-lang-menu gebundener Listener würde
        // nach dem ersten Sprachwechsel auf verwaiste Elemente zeigen bzw. sich
        // bei jedem Rebuild ein weiteres Mal auf document registrieren.
        this.modalElement.addEventListener('click', (e) => {
            const toggle = e.target.closest('.pixelmate-lang-toggle');
            const menu = this.modalElement.querySelector('.pixelmate-lang-menu');
            if (!menu) return;

            if (toggle) {
                e.stopPropagation();
                const isOpen = menu.style.display === 'block';
                menu.style.display = isOpen ? 'none' : 'block';
                toggle.setAttribute('aria-expanded', String(!isOpen));
                return;
            }

            // Klick auf eine Sprachoption schließt das Dropdown wieder -
            // switchLanguage() baut das Modal ohnehin per rebuildModal() neu auf.
            if (e.target.closest('.pixelmate-lang-btn')) {
                menu.style.display = 'none';
                this.modalElement.querySelector('.pixelmate-lang-toggle')?.setAttribute('aria-expanded', 'false');
            }
        });

        document.addEventListener('click', (e) => {
            const menu = this.modalElement?.querySelector('.pixelmate-lang-menu');
            if (menu && menu.style.display === 'block' && !this.modalElement.contains(e.target)) {
                menu.style.display = 'none';
                this.modalElement.querySelector('.pixelmate-lang-toggle')?.setAttribute('aria-expanded', 'false');
            }
        });

        document.addEventListener('keydown', (e) => {
            if (e.key !== 'Escape') return;
            const menu = this.modalElement?.querySelector('.pixelmate-lang-menu');
            if (menu && menu.style.display === 'block') {
                menu.style.display = 'none';
                this.modalElement.querySelector('.pixelmate-lang-toggle')?.setAttribute('aria-expanded', 'false');
            }
        });

        // Event Listeners
        this.attachModalListeners();
    }

    /**
     * Modal HTML generieren
     */
    /**
     * Reihenfolge von "Alle akzeptieren"/"Alles ablehnen" im Footer des
     * Cookie-Einstellungen-Modals - folgt derselben Einstellung wie im
     * Haupt-Banner (pixelmate_button_display_order, siehe "Banner Texte"-
     * Karte der Haupteinstellungen / PixelmateBanner.getButtonsHTML()).
     * Default ist "Alles ablehnen" zuerst.
     */
    getModalActionButtonsHTML() {
        const rejectButtonHTML = `<button class="pixelmate-modal-reject-all" aria-label="${this.localizedBannerText('rejectButton', 'rejectAll')}" style="
                    background: ${this.design.buttons?.reject?.background || '#0073aa'} !important;
                    color: ${this.design.buttons?.reject?.text || '#ffffff'} !important;
                    border: none !important;
                    box-sizing: border-box !important;
                    padding: 12px 24px !important;
                    border-radius: 4px !important;
                    font-size: 14px !important;
                    font-weight: 600 !important;
                    line-height: 1em !important;
                    height: auto !important;
                    cursor: pointer !important;
                    transition: opacity 0.2s !important;
                " ${this.getModalButtonHoverAttrs(this.design.buttons?.reject || {})}>
                    ${this.localizedBannerText('rejectButton', 'rejectAll')}
                </button>`;

        const acceptButtonHTML = `<button class="pixelmate-modal-accept-all" aria-label="${this.texts.acceptAll || this.t('acceptAll')}" style="
                    background: ${this.design.buttons?.accept?.background || '#0073aa'} !important;
                    color: ${this.design.buttons?.accept?.text || '#ffffff'} !important;
                    border: none !important;
                    box-sizing: border-box !important;
                    padding: 12px 24px !important;
                    border-radius: 4px !important;
                    font-size: 14px !important;
                    font-weight: 600 !important;
                    line-height: 1em !important;
                    height: auto !important;
                    cursor: pointer !important;
                    transition: opacity 0.2s !important;
                " ${this.getModalButtonHoverAttrs(this.design.buttons?.accept || {})}>
                    ${this.texts.acceptAll || this.t('acceptAll')}
                </button>`;

        return this.config.settings?.buttonDisplayOrder === 'accept_first'
            ? acceptButtonHTML + rejectButtonHTML
            : rejectButtonHTML + acceptButtonHTML;
    }

    getModalHTML() {
        return `
            <!-- Schließen-Button absolut oben rechts -->
            <button class="pixelmate-modal-close" style="
                position: absolute !important;
                top: 14px !important;
                right: 16px !important;
                background: none !important;
                border: none !important;
                font-size: 26px !important;
                cursor: pointer !important;
                padding: 0 !important;
                width: 32px !important;
                height: 32px !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                color: ${this.design.colors?.text || '#333333'} !important;
                opacity: 0.45 !important;
                transition: opacity 0.2s !important;
                z-index: 1 !important;
                line-height: 1 !important;
            " onmouseover="this.style.opacity='0.9'" onmouseout="this.style.opacity='0.45'">
                ×
            </button>

            <div class="pixelmate-modal-header" style="
                padding: 28px 24px 0 24px !important;
                border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
                flex-shrink: 0 !important;
            ">
                <div style="margin-bottom: 12px !important; padding-right: 32px !important;">
                    <h2 id="pixelmate-modal-title" style="margin: 0 !important; font-size: 20px !important; font-weight: 600 !important;">
                        ${this.texts.settingsTitle || this.t('settingsTitle')}
                    </h2>
                </div>

                <!-- Tab Navigation -->
                <div class="pixelmate-modal-tabs" style="
                    display: flex !important;
                    gap: 8px !important;
                    margin-bottom: -1px !important;
                ">
                    <button class="pixelmate-tab-btn" data-tab="settings" style="
                        background: none !important;
                        border: none !important;
                        border-bottom: 3px solid ${this.design.buttons?.accept?.background || '#0073aa'} !important;
                        box-sizing: border-box !important;
                        padding: 12px 20px !important;
                        cursor: pointer !important;
                        font-size: 14px !important;
                        font-weight: 600 !important;
                        line-height: 1em !important;
                        height: auto !important;
                        color: ${this.design.buttons?.accept?.background || '#0073aa'} !important;
                        transition: all 0.2s !important;
                    ">
                        ${this.t('tabSettings')}
                    </button>
                    <button class="pixelmate-tab-btn" data-tab="datenauskunft" style="
                        background: none !important;
                        border: none !important;
                        border-bottom: 3px solid transparent !important;
                        box-sizing: border-box !important;
                        padding: 12px 20px !important;
                        cursor: pointer !important;
                        font-size: 14px !important;
                        font-weight: 600 !important;
                        line-height: 1em !important;
                        height: auto !important;
                        color: #666666 !important;
                        transition: all 0.2s !important;
                    ">
                        ${this.t('tabDataInfo')}
                    </button>
                </div>
            </div>

            <div class="pixelmate-modal-body" style="
                padding: 16px 24px !important;
                max-height: 100vh !important;
                overflow-y: scroll !important;
                flex: 1 !important;
                min-height: 0 !important;
            ">
                <!-- Settings Tab Content -->
                <div class="pixelmate-tab-content" data-tab-content="settings" style="display: block !important;">
                    <p style="margin: 0 0 16px 0 !important; font-size: 14px !important; color: ${this.design.colors?.text || '#666666'} !important; opacity: 0.75 !important;">
                        ${this.texts.settingsDescription || this.t('settingsDescription')}
                    </p>

                    ${this.getCategoriesHTML()}
                </div>

                <!-- Datenauskunft Tab Content -->
                <div class="pixelmate-tab-content" data-tab-content="datenauskunft" style="display: none !important;">
                    <div class="pixelmate-datenauskunft-container">
                        ${this.getDatenauskunftHTML()}
                    </div>
                </div>
            </div>

            <div class="pixelmate-modal-footer" style="
                padding: 16px 24px !important;
                border-top: 1px solid rgba(0, 0, 0, 0.1) !important;
                display: flex !important;
                gap: 10px !important;
                justify-content: flex-end !important;
                background: rgba(0, 0, 0, 0.02) !important;
                flex-shrink: 0 !important;
            ">
                ${this.getModalActionButtonsHTML()}

                <button class="pixelmate-modal-save-selection" aria-label="${this.texts.saveSelection || this.t('saveSelection')}" style="
                    background: ${this.design.buttons?.accept?.background || '#0073aa'} !important;
                    color: ${this.design.buttons?.accept?.text || '#ffffff'} !important;
                    border: none !important;
                    box-sizing: border-box !important;
                    padding: 12px 24px !important;
                    border-radius: 4px !important;
                    font-size: 14px !important;
                    font-weight: 600 !important;
                    line-height: 1em !important;
                    height: auto !important;
                    cursor: pointer !important;
                    transition: opacity 0.2s !important;
                " ${this.getModalButtonHoverAttrs(this.design.buttons?.accept || {})}>
                    ${this.texts.saveSelection || this.t('saveSelection')}
                </button>

                <button class="pixelmate-modal-close-footer" aria-label="${this.t('closeButton')}" style="
                    background: none !important;
                    color: ${this.design.colors?.text || '#333333'} !important;
                    border: 1px solid rgba(0, 0, 0, 0.15) !important;
                    box-sizing: border-box !important;
                    padding: 12px 24px !important;
                    border-radius: 4px !important;
                    font-size: 14px !important;
                    font-weight: 600 !important;
                    line-height: 1em !important;
                    height: auto !important;
                    cursor: pointer !important;
                    transition: opacity 0.2s !important;
                " onmouseover="this.style.opacity='0.75'" onmouseout="this.style.opacity='1'">
                    ${this.t('closeButton')}
                </button>
            </div>

            ${this.getFooterLinksHTML()}

            <!-- Sprach-Umschalter: Globus-Icon + "Lang" öffnet per Klick ein
                 Dropdown mit den einzelnen Sprachoptionen, genau wie im
                 Haupt-Banner (siehe PixelmateBanner::getLanguageSwitcherHTML()
                 - dasselbe Klassennamen-/Interaktionsmuster für konsistente
                 Optik, in attachModalListeners() analog verdrahtet). -->
            <div class="pixelmate-modal-lang-footer" style="
                padding: 6px 24px !important;
                border-top: 1px solid rgba(0, 0, 0, 0.06) !important;
                display: flex !important;
                justify-content: start !important;
                align-items: center !important;
                flex-shrink: 0 !important;
            ">
                ${this.getModalLanguageSwitcherHTML()}
            </div>
        `;
    }

    /**
     * Sprach-Umschalter im selben Stil wie PixelmateBanner::getLanguageSwitcherHTML()
     * - kompakter Globus-Icon-Toggle statt einer dauerhaft sichtbaren Zeile
     * aller 9 Sprachkürzel.
     */
    getModalLanguageSwitcherHTML() {
        const textColor = this.design.colors?.text || '#333333';
        const pmLangs = ['de', 'en', 'fr', 'es', 'it', 'nl', 'pl', 'da', 'sv'];

        const optionsHTML = pmLangs.map(lang => {
            const isActive = lang === this.currentLang;
            return `
                <button type="button" class="pixelmate-lang-btn" data-lang="${lang}" role="menuitemradio" aria-checked="${isActive}" style="
                    display: block !important;
                    width: 100% !important;
                    box-sizing: border-box !important;
                    text-align: left !important;
                    background: ${isActive ? 'rgba(128,128,128,0.15)' : 'none'} !important;
                    border: none !important;
                    padding: 6px 14px !important;
                    font-size: 12px !important;
                    font-weight: ${isActive ? '700' : '400'} !important;
                    color: ${textColor} !important;
                    cursor: pointer !important;
                    letter-spacing: 0.5px !important;
                    text-transform: uppercase !important;
                    white-space: nowrap !important;
                    font-family: inherit !important;
                " onmouseover="this.style.background='rgba(128,128,128,0.15)'" onmouseout="this.style.background='${isActive ? 'rgba(128,128,128,0.15)' : 'none'}'">
                    ${lang}
                </button>
            `;
        }).join('');

        return `
        <div class="pixelmate-language-switcher" style="position: relative !important; display: inline-flex !important; align-items: center !important;">
            <button type="button" class="pixelmate-lang-toggle" aria-haspopup="true" aria-expanded="false" style="
                display: inline-flex !important;
                align-items: center !important;
                gap: 4px !important;
                background: none !important;
                border: none !important;
                padding: 2px 4px !important;
                font-size: 11px !important;
                font-weight: 400 !important;
                color: ${textColor} !important;
                opacity: 0.75 !important;
                cursor: pointer !important;
                letter-spacing: 0.3px !important;
                text-transform: uppercase !important;
                line-height: 1 !important;
                font-family: inherit !important;
            ">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink: 0 !important;" aria-hidden="true">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="2" y1="12" x2="22" y2="12"></line>
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                </svg>
                <span>${this.currentLang}</span>
            </button>
            <div class="pixelmate-lang-menu" role="menu" style="
                display: none !important;
                position: absolute !important;
                bottom: 100% !important;
                left: 0 !important;
                margin-bottom: 6px !important;
                background: #ffffff !important;
                border: 1px solid rgba(0,0,0,0.1) !important;
                border-radius: 6px !important;
                box-shadow: 0 2px 12px rgba(0,0,0,0.18) !important;
                min-width: 88px !important;
                overflow: hidden !important;
                z-index: 10 !important;
            ">
                ${optionsHTML}
            </div>
        </div>
        `;
    }

    /**
     * Erzeugt onmouseover/onmouseout Attribute für einen Footer-Button. Sind
     * eigene Hover-Farben konfiguriert, werden Hintergrund-/Textfarbe
     * getauscht, ansonsten dient eine reduzierte Deckkraft als Hover-Feedback.
     */
    getModalButtonHoverAttrs(buttonConfig) {
        const normalBg = buttonConfig.background || '#0073aa';
        const normalText = buttonConfig.text || '#ffffff';
        const hoverBg = buttonConfig.hoverBackground || '';
        const hoverText = buttonConfig.hoverText || '';

        if (!hoverBg && !hoverText) {
            return `onmouseover="this.style.opacity='0.9'" onmouseout="this.style.opacity='1'"`;
        }

        const overStyles = [
            `background-color: ${hoverBg || normalBg} !important`,
            `color: ${hoverText || normalText} !important`
        ].join('; ');
        const outStyles = [
            `background-color: ${normalBg} !important`,
            `color: ${normalText} !important`
        ].join('; ');

        return `onmouseover="this.style.cssText += '; ${overStyles}'" onmouseout="this.style.cssText += '; ${outStyles}'"`;
    }

    /**
     * Datenschutz-/Impressum-Links im Footer des Settings Modals,
     * analog zu den Links im Cookie Banner.
     */
    getFooterLinksHTML() {
        const privacyUrl = typeof this.banner.getPrivacyUrl === 'function' ? this.banner.getPrivacyUrl() : (this.banner.urls?.privacy || null);
        const imprintUrl = typeof this.banner.getImprintUrl === 'function' ? this.banner.getImprintUrl() : (this.banner.urls?.imprint || null);

        if (!privacyUrl && !imprintUrl) {
            return '';
        }

        const linkColor = this.design.colors?.link || '#0073aa';
        const links = [];

        if (privacyUrl) {
            links.push(`<a href="${privacyUrl}" target="_blank" rel="noopener" style="color: ${linkColor} !important; text-decoration: underline !important;">${this.localizedBannerText('privacyLink', 'privacyLink')}<span class="pixelmate-sr-only">${this.t('opensInNewTab')}</span></a>`);
        }

        if (imprintUrl) {
            links.push(`<a href="${imprintUrl}" target="_blank" rel="noopener" style="color: ${linkColor} !important; text-decoration: underline !important;">${this.localizedBannerText('imprintLink', 'imprintLink')}<span class="pixelmate-sr-only">${this.t('opensInNewTab')}</span></a>`);
        }

        return `
            <div class="pixelmate-modal-footer-links" style="
                padding: 8px 24px !important;
                border-top: 1px solid rgba(0, 0, 0, 0.06) !important;
                display: flex !important;
                justify-content: center !important;
                align-items: center !important;
                gap: 10px !important;
                flex-shrink: 0 !important;
                font-size: 12px !important;
            ">
                ${links.join('<span style="opacity: 0.5 !important;">|</span>')}
            </div>
        `;
    }

    /**
     * Kategorien HTML generieren (Material Design M3 List Style)
     */
    getCategoriesHTML() {
        let html = '<div style="border-top: 1px solid rgba(0, 0, 0, 0.08) !important;">';

        const textColor = this.design.colors?.text || '#333333';

        const showCategories = this.config.settings?.showCategories || {
            necessary: true, analytics: true, marketing: true, external: true
        };

        Object.keys(this.categories).forEach(categoryKey => {
            const category = this.categories[categoryKey];

            if (!showCategories[categoryKey]) {
                return;
            }

            if (categoryKey !== 'necessary' && category.services.length === 0) {
                return;
            }

            const hasServices = category.services.length > 0;

            html += `
                <div class="pixelmate-category" style="border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;">
                    <div style="
                        display: flex !important;
                        align-items: flex-start !important;
                        padding: 14px 0 !important;
                        gap: 12px !important;
                    ">
                        <div class="pixelmate-category-header" data-category-id="${categoryKey}" style="
                            flex: 1 !important;
                            min-width: 0 !important;
                            ${hasServices ? 'cursor: pointer !important;' : ''}
                        ">
                            <div style="display: flex !important; align-items: center !important; gap: 8px !important; margin-bottom: 3px !important;">
                                ${hasServices ? `
                                    <span class="pixelmate-category-expand-icon" data-category-id="${categoryKey}"
                                        role="button" tabindex="0" aria-expanded="false"
                                        aria-label="${this.t(`categories.${categoryKey}.name`)}"
                                        style="
                                        display: inline-block !important;
                                        width: 9px !important;
                                        height: 9px !important;
                                        border-right: 2px solid ${textColor} !important;
                                        border-bottom: 2px solid ${textColor} !important;
                                        opacity: 0.5 !important;
                                        transform: rotate(-45deg) !important;
                                        transition: transform 0.25s ease !important;
                                        flex-shrink: 0 !important;
                                        margin-top: 1px !important;
                                    "></span>
                                ` : `<span style="width: 9px !important; display: inline-block !important; flex-shrink: 0 !important;"></span>`}
                                <span style="
                                    font-size: 16px !important;
                                    font-weight: 600 !important;
                                    color: ${textColor} !important;
                                    line-height: 1.3 !important;
                                ">${this.t(`categories.${categoryKey}.name`)}</span>
                                ${category.required ? `
                                    <span style="
                                        font-size: 11px !important;
                                        color: ${textColor} !important;
                                        opacity: 0.5 !important;
                                        font-weight: 500 !important;
                                    ">(${this.t('required')})</span>
                                ` : ''}
                            </div>
                            <div style="
                                font-size: 13px !important;
                                color: ${textColor} !important;
                                opacity: 0.6 !important;
                                line-height: 1.4 !important;
                                padding-left: 17px !important;
                            ">${this.t(`categories.${categoryKey}.description`)}</div>
                        </div>

                        <label class="pixelmate-toggle" style="
                            position: relative !important;
                            display: inline-block !important;
                            width: 44px !important;
                            height: 24px !important;
                            flex-shrink: 0 !important;
                            margin-top: 3px !important;
                            ${category.required ? 'opacity: 0.5 !important; cursor: not-allowed !important;' : 'cursor: pointer !important;'}
                        ">
                            <input
                                type="checkbox"
                                data-category="${categoryKey}"
                                aria-label="${this.t(`categories.${categoryKey}.name`)}"
                                ${category.required ? 'checked disabled' : ''}
                                style="opacity: 0 !important; width: 0 !important; height: 0 !important;"
                            >
                            <span class="pixelmate-toggle-slider" style="
                                position: absolute !important;
                                cursor: ${category.required ? 'not-allowed' : 'pointer'} !important;
                                top: 0 !important;
                                left: 0 !important;
                                right: 0 !important;
                                bottom: 0 !important;
                                background-color: #ccc !important;
                                transition: 0.3s !important;
                                border-radius: 24px !important;
                            "></span>
                        </label>
                    </div>

                    <div class="pixelmate-category-services" data-category-id="${categoryKey}" style="
                        max-height: 0 !important;
                        overflow: hidden !important;
                        transition: max-height 0.32s ease !important;
                    ">
                        ${this.getServicesHTML(category.services, category.required)}
                    </div>
                </div>
            `;
        });

        html += '</div>';
        return html;
    }

    /**
     * Datenauskunft HTML generieren (initiales Ladeplatzhalter)
     */
    getDatenauskunftHTML() {
        return `
            <div style="margin-bottom: 20px !important;">
                <h3 style="margin: 0 0 12px 0 !important; font-size: 18px !important; font-weight: 600 !important;">
                    ${this.t('dataInfoTitle')}
                </h3>
                <p style="margin: 0 0 16px 0 !important; color: #666 !important; font-size: 14px !important;">
                    ${this.t('dataInfoDesc')}
                </p>
            </div>
            <div class="pixelmate-datenauskunft-results" style="min-height: 100px !important;">
                <p style="color: #999 !important; text-align: center !important; padding: 40px 0 !important;">
                    ${this.t('loading')}
                </p>
            </div>
        `;
    }

    /**
     * Browsergebundene Protokolldaten sicher vom Server laden
     */
    async loadDatenauskunft() {
        const container = this.modalElement?.querySelector('.pixelmate-datenauskunft-results');
        if (!container) return;

        container.innerHTML = `<p style="color:#999 !important;text-align:center !important;padding:40px 0 !important;">${this.t('loading')}</p>`;

        try {
            if (!this.config?.ajax?.url) {
                container.innerHTML = `<p style="color:#c00 !important;padding:20px 0 !important;">${this.t('configUnavailable')}</p>`;
                return;
            }

            // Über den Banner senden: der holt bei abgelaufener Nonce (gecachte Seite)
            // automatisch eine frische nach und wiederholt die Anfrage einmal.
            const data = await this.banner.ajaxPost('pixelmate_get_ip_data');

            if (!data || !data.success) {
                container.innerHTML = `<p style="color:#c00 !important;padding:20px 0 !important;">${this.t('errorLoading')}</p>`;
                return;
            }

            const entries = Array.isArray(data.data?.entries) ? data.data.entries : [];
            const accentColor = this.design.buttons?.accept?.background || '#0073aa';

            let html = '';

            if (entries.length === 0) {
                html += `<p style="color:#999 !important;text-align:center !important;padding:20px 0 !important;">${this.t('noEntries')}</p>`;
            } else {
                html += `<h4 style="margin:0 0 12px 0 !important;font-size:15px !important;font-weight:600 !important;">${this.t('storedEntries')} (${entries.length})</h4>`;

                entries.forEach((entry, idx) => {
                    const consentSummary = this.formatConsentData(entry.consent_data);
                    html += `
                        <div style="margin-bottom:12px !important;padding:14px !important;border:1px solid rgba(0,0,0,0.08) !important;border-radius:6px !important;background:#fff !important;">
                            <div style="display:flex !important;justify-content:space-between !important;align-items:center !important;margin-bottom:8px !important;">
                                <span style="font-size:12px !important;color:#999 !important;">${this.escapeHtml(entry.timestamp)}</span>
                                <span style="font-size:11px !important;font-weight:600 !important;padding:2px 8px !important;border-radius:20px !important;background:${accentColor}20 !important;color:${accentColor} !important;">${this.escapeHtml(entry.consent_type)}</span>
                            </div>
                            <div style="font-size:13px !important;color:#444 !important;margin-bottom:6px !important;">${consentSummary}</div>
                            <div style="font-size:12px !important;color:#aaa !important;word-break:break-all !important;">${this.escapeHtml(entry.page_url)}</div>
                        </div>
                    `;
                });
            }

            container.innerHTML = html;
        } catch (err) {
            container.innerHTML = `<p style="color:#c00 !important;padding:20px 0 !important;">${this.t('errorGeneric')} ${this.escapeHtml(err.message)}</p>`;
        }
    }

    /**
     * Consent-Daten als Raw-String + Dienst-Chips formatieren
     */
    formatConsentData(consentDataRaw) {
        const rawStr = typeof consentDataRaw === 'string'
            ? consentDataRaw
            : JSON.stringify(consentDataRaw);

        const rawHtml = `<pre style="margin:0 0 10px 0 !important;font-size:11px !important;color:#555 !important;white-space:pre-wrap !important;word-break:break-all !important;background:rgba(0,0,0,0.03) !important;padding:8px !important;border-radius:4px !important;font-family:monospace !important;">${this.escapeHtml(rawStr)}</pre>`;

        let parsed = null;
        try { parsed = JSON.parse(rawStr); } catch (_) { /* not valid JSON */ }
        if (!parsed) return rawHtml;

        const accentColor = this.design.buttons?.accept?.background || '#0073aa';
        const chipStyle = `display:inline-block !important;font-size:11px !important;font-weight:600 !important;padding:2px 10px !important;border-radius:20px !important;margin:2px 4px 2px 0 !important;background:${accentColor}20 !important;color:${accentColor} !important;`;

        const categories    = parsed.categories || {};
        const services      = parsed.services   || {};
        const allCatKeys    = ['necessary', 'analytics', 'marketing', 'external'];
        const allAccepted   = allCatKeys.every(k => categories[k] === true);

        const categoryNames = {
            necessary: { de: 'Notwendig',       en: 'Necessary',        fr: 'Nécessaire', es: 'Necesario', it: 'Necessari', nl: 'Noodzakelijk', pl: 'Niezbędne', da: 'Nødvendige', sv: 'Nödvändiga' },
            analytics: { de: 'Analyse',          en: 'Analytics',        fr: 'Analyse', es: 'Estadísticas', it: 'Statistiche', nl: 'Statistieken', pl: 'Statystyka', da: 'Statistik', sv: 'Statistik' },
            marketing: { de: 'Marketing',        en: 'Marketing',        fr: 'Marketing', es: 'Marketing', it: 'Marketing', nl: 'Marketing', pl: 'Marketing', da: 'Marketing', sv: 'Marknadsföring' },
            external:  { de: 'Externe Inhalte',  en: 'External Content', fr: 'Contenus externes', es: 'Contenido externo', it: 'Contenuti esterni', nl: 'Externe inhoud', pl: 'Treści zewnętrzne', da: 'Eksternt indhold', sv: 'Externt innehåll' },
        };
        const lang = this.currentLang || 'de';

        let chips = '';
        if (allAccepted) {
            chips = `<span style="${chipStyle}">${this.t('allCookiesAccepted')}</span>`;
        } else {
            allCatKeys.forEach(key => {
                if (categories[key]) {
                    const label = categoryNames[key]?.[lang] ?? categoryNames[key]?.de ?? key;
                    chips += `<span style="${chipStyle}">${this.escapeHtml(label)}</span>`;
                }
            });
        }

        // Individuell freigegebene Dienste (z.B. YouTube via Content-Blocker)
        Object.entries(services).forEach(([svcId, val]) => {
            if (!val) return;
            const extra = SettingsModal.EXTRA_SERVICE_INFO[svcId];
            const label = extra?.name || svcId.replace(/_/g, ' ');
            chips += `<span style="${chipStyle}">${this.escapeHtml(label)}</span>`;
        });

        if (!chips) return rawHtml;

        return rawHtml + `<div style="margin-top:4px !important;"><span style="font-size:11px !important;color:#999 !important;margin-right:6px !important;">${this.t('consentServices')}:</span>${chips}</div>`;
    }

    /**
     * HTML-Zeichen escapen
     */
    escapeHtml(str) {
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    /**
     * Alle Cookies auf der aktuellen Seite scannen
     */
    scanCurrentCookies() {
        const cookies = document.cookie.split(';').map(cookie => {
            const [name, ...valueParts] = cookie.trim().split('=');
            return {
                name: name.trim(),
                value: valueParts.join('=')
            };
        }).filter(cookie => cookie.name);

        return cookies;
    }

    /**
     * Cookie in der Datenbank suchen
     */
    findCookieInDatabase(cookieName) {
        if (!this.cookieDatabase) return null;

        const results = [];

        // Durch alle Services iterieren
        Object.keys(this.cookieDatabase).forEach(serviceName => {
            const serviceCookies = this.cookieDatabase[serviceName];

            serviceCookies.forEach(cookieData => {
                // Exakte Übereinstimmung
                if (cookieData.cookie === cookieName) {
                    results.push({
                        service: serviceName,
                        ...cookieData
                    });
                }
                // Wildcard-Match (wenn wildcardMatch = 1)
                else if (cookieData.wildcardMatch === "1") {
                    // Wildcard-Matching implementieren
                    const pattern = cookieData.cookie.replace(/\*/g, '.*');
                    const regex = new RegExp(`^${pattern}$`, 'i');
                    if (regex.test(cookieName)) {
                        results.push({
                            service: serviceName,
                            ...cookieData,
                            matchType: 'wildcard'
                        });
                    }
                }
            });
        });

        return results.length > 0 ? results : null;
    }

    /**
     * Cookie-Scan-Ergebnisse anzeigen
     */
    displayCookieScanResults() {
        const resultsContainer = this.modalElement.querySelector('.pixelmate-cookie-scan-results');
        if (!resultsContainer) return;

        const cookies = this.scanCurrentCookies();

        if (cookies.length === 0) {
            resultsContainer.innerHTML = `
                <p style="color: #999 !important; text-align: center !important; padding: 40px 0 !important;">
                    Keine Cookies gefunden.
                </p>
            `;
            return;
        }

        let html = `
            <div style="margin-bottom: 16px !important;">
                <strong style="font-size: 16px !important;">
                    ${cookies.length} Cookie(s) gefunden
                </strong>
            </div>
        `;

        cookies.forEach(cookie => {
            const cookieInfo = this.findCookieInDatabase(cookie.name);

            if (cookieInfo && cookieInfo.length > 0) {
                // Cookie in Datenbank gefunden
                cookieInfo.forEach(info => {
                    html += this.renderCookieInfo(cookie.name, info);
                });
            } else {
                // Cookie nicht in Datenbank gefunden
                html += this.renderUnknownCookie(cookie.name);
            }
        });

        resultsContainer.innerHTML = html;
    }

    /**
     * Render Cookie-Info aus Datenbank
     */
    renderCookieInfo(cookieName, info) {
        const categoryColors = {
            'Functional': '#0073aa',
            'Analytics': '#ff9800',
            'Marketing': '#e91e63',
            'Necessary': '#4caf50'
        };

        const categoryColor = categoryColors[info.category] || '#666';

        return `
            <div style="
                margin-bottom: 16px !important;
                padding: 16px !important;
                border: 1px solid rgba(0, 0, 0, 0.1) !important;
                border-radius: 8px !important;
                background: white !important;
            ">
                <div style="display: flex !important; justify-content: space-between !important; align-items: start !important; margin-bottom: 12px !important;">
                    <div>
                        <h4 style="margin: 0 0 4px 0 !important; font-size: 16px !important; font-weight: 600 !important; color: #333 !important;">
                            ${cookieName}
                            ${info.matchType === 'wildcard' ? '<span style="font-size: 11px !important; color: #999 !important; font-weight: normal !important;">(Wildcard-Match)</span>' : ''}
                        </h4>
                        <div style="font-size: 13px !important; color: #666 !important;">
                            Service: <strong>${info.service}</strong>
                        </div>
                    </div>
                    <span style="
                        padding: 4px 12px !important;
                        background: ${categoryColor} !important;
                        color: white !important;
                        border-radius: 12px !important;
                        font-size: 11px !important;
                        font-weight: 600 !important;
                        text-transform: uppercase !important;
                    ">
                        ${info.category}
                    </span>
                </div>

                <div style="margin-bottom: 12px !important;">
                    <p style="margin: 0 !important; font-size: 14px !important; color: #666 !important; line-height: 1.5 !important;">
                        ${this.getCookieDescription(info)}
                    </p>
                </div>

                <div style="
                    display: grid !important;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)) !important;
                    gap: 12px !important;
                    padding: 12px !important;
                    background: rgba(0, 0, 0, 0.02) !important;
                    border-radius: 4px !important;
                    font-size: 13px !important;
                ">
                    ${info.domain ? `
                        <div>
                            <strong style="color: #333 !important;">Domain:</strong><br>
                            <span style="color: #666 !important;">${info.domain}</span>
                        </div>
                    ` : ''}
                    ${info.retentionPeriod ? `
                        <div>
                            <strong style="color: #333 !important;">Speicherdauer:</strong><br>
                            <span style="color: #666 !important;">${info.retentionPeriod}</span>
                        </div>
                    ` : ''}
                    ${info.dataController ? `
                        <div>
                            <strong style="color: #333 !important;">Anbieter:</strong><br>
                            <span style="color: #666 !important;">${info.dataController}</span>
                        </div>
                    ` : ''}
                </div>

                ${info.privacyLink ? `
                    <div style="margin-top: 12px !important;">
                        <a href="${info.privacyLink}" target="_blank" rel="noopener noreferrer" style="
                            color: ${this.design.colors?.link || '#0073aa'} !important;
                            font-size: 13px !important;
                            text-decoration: none !important;
                            font-weight: 600 !important;
                        " onmouseover="this.style.textDecoration='underline'" onmouseout="this.style.textDecoration='none'">
                            Datenschutzrichtlinie →<span class="pixelmate-sr-only">${this.t('opensInNewTab')}</span>
                        </a>
                    </div>
                ` : ''}
            </div>
        `;
    }

    /**
     * Render unbekanntes Cookie
     */
    renderUnknownCookie(cookieName) {
        return `
            <div style="
                margin-bottom: 16px !important;
                padding: 16px !important;
                border: 1px solid rgba(0, 0, 0, 0.1) !important;
                border-radius: 8px !important;
                background: rgba(255, 152, 0, 0.05) !important;
            ">
                <div style="display: flex !important; justify-content: space-between !important; align-items: start !important; margin-bottom: 8px !important;">
                    <h4 style="margin: 0 !important; font-size: 16px !important; font-weight: 600 !important; color: #333 !important;">
                        ${cookieName}
                    </h4>
                    <span style="
                        padding: 4px 12px !important;
                        background: #ff9800 !important;
                        color: white !important;
                        border-radius: 12px !important;
                        font-size: 11px !important;
                        font-weight: 600 !important;
                        text-transform: uppercase !important;
                    ">
                        Unbekannt
                    </span>
                </div>
                <p style="margin: 0 !important; font-size: 14px !important; color: #666 !important;">
                    Keine Informationen zu diesem Cookie in der Datenbank gefunden.
                </p>
            </div>
        `;
    }

    /**
     * Services HTML generieren (Material Design M3 List Style)
     */
    getServicesHTML(services, categoryRequired = false) {
        if (services.length === 0) return '';

        const textColor = this.design.colors?.text || '#333333';
        const linkColor = this.design.colors?.link || this.design.buttons?.accept?.background || '#0073aa';

        let html = '<div style="padding-bottom: 6px !important;">';

        services.forEach(service => {
            let cookieInfo = this.getServiceCookieInfo(service);
            let paymentFallback = false;

            // Zahlungsanbieter ohne eigene Cookie-Einträge (siehe
            // PAYMENT_SERVICE_IDS): statt den Aufklapp-Pfeil stillschweigend
            // wegzulassen (liest sich wie "keine Cookies vorhanden"), einen
            // neutralen Hinweis anzeigen, dass der Anbieter eigene Cookies
            // setzen kann - ohne Cookie-Namen zu erfinden.
            if ((!cookieInfo || cookieInfo.length === 0) && SettingsModal.PAYMENT_SERVICE_IDS.has(service.id)) {
                cookieInfo = true;
                paymentFallback = true;
            }

            const toggleHTML = categoryRequired ? `
                <span style="
                    flex-shrink: 0 !important;
                    padding: 2px 8px !important;
                    border-radius: 10px !important;
                    background: rgba(0, 0, 0, 0.06) !important;
                    font-size: 11px !important;
                    font-weight: 500 !important;
                    color: ${textColor} !important;
                    opacity: 0.6 !important;
                    white-space: nowrap !important;
                ">${this.t('required')}</span>
            ` : `
                <label class="pixelmate-service-toggle" style="
                    position: relative !important;
                    display: inline-block !important;
                    width: 44px !important;
                    height: 24px !important;
                    cursor: pointer !important;
                    flex-shrink: 0 !important;
                ">
                    <input
                        type="checkbox"
                        data-service="${service.id}"
                        aria-label="${service.name}"
                        style="opacity: 0 !important; width: 0 !important; height: 0 !important;"
                    >
                    <span class="pixelmate-service-toggle-slider" style="
                        position: absolute !important;
                        cursor: pointer !important;
                        top: 0 !important;
                        left: 0 !important;
                        right: 0 !important;
                        bottom: 0 !important;
                        background-color: #ccc !important;
                        transition: 0.3s !important;
                        border-radius: 24px !important;
                    "></span>
                </label>
            `;

            html += `
                <div class="pixelmate-service" data-service-id="${service.id}" style="
                    border-top: 1px solid rgba(0, 0, 0, 0.06) !important;
                    padding: 10px 0 10px 24px !important;
                ">
                    <div style="display: flex !important; align-items: center !important; gap: 10px !important;">
                        <div class="pixelmate-service-header" data-service-id="${service.id}" style="
                            flex: 1 !important;
                            min-width: 0 !important;
                            ${cookieInfo ? 'cursor: pointer !important;' : ''}
                        ">
                            <div style="display: flex !important; align-items: center !important; gap: 7px !important;">
                                ${cookieInfo ? `
                                    <span class="pixelmate-service-expand-icon" data-service-id="${service.id}"
                                        role="button" tabindex="0" aria-expanded="false"
                                        aria-label="${service.name}"
                                        style="
                                        display: inline-block !important;
                                        width: 7px !important;
                                        height: 7px !important;
                                        border-right: 1.5px solid ${textColor} !important;
                                        border-bottom: 1.5px solid ${textColor} !important;
                                        opacity: 0.45 !important;
                                        transform: rotate(-45deg) !important;
                                        transition: transform 0.22s ease !important;
                                        flex-shrink: 0 !important;
                                        margin-top: 1px !important;
                                    "></span>
                                ` : ''}
                                <span style="
                                    font-size: 14px !important;
                                    font-weight: 500 !important;
                                    color: ${textColor} !important;
                                    line-height: 1.3 !important;
                                ">${service.name}</span>
                            </div>
                            ${(service.description || service.provider || service.privacy_url) ? `
                                <div style="
                                    font-size: 12px !important;
                                    color: ${textColor} !important;
                                    opacity: 0.55 !important;
                                    margin-top: 2px !important;
                                    line-height: 1.4 !important;
                                    ${cookieInfo ? 'padding-left: 14px !important;' : ''}
                                ">
                                    ${service.description ? service.description : ''}
                                    ${service.description && (service.provider || service.privacy_url) ? ' · ' : ''}
                                    ${service.provider || ''}
                                    ${service.provider && service.privacy_url ? ' · ' : ''}
                                    ${service.privacy_url ? `<a href="${service.privacy_url}" target="_blank" rel="noopener noreferrer" style="color: ${linkColor} !important; text-decoration: none !important;" onmouseover="this.style.textDecoration='underline'" onmouseout="this.style.textDecoration='none'">${this.t('privacy')}<span class="pixelmate-sr-only">${this.t('opensInNewTab')}</span></a>` : ''}
                                </div>
                            ` : ''}
                        </div>
                        ${toggleHTML}
                    </div>

                    ${cookieInfo ? `
                        <div class="pixelmate-service-details" data-service-id="${service.id}" style="
                            max-height: 0 !important;
                            overflow: hidden !important;
                            transition: max-height 0.25s ease !important;
                            padding-left: 14px !important;
                        ">
                            ${paymentFallback ? this.renderPaymentCookieFallback() : this.renderServiceCookies(cookieInfo)}
                        </div>
                    ` : ''}
                </div>
            `;
        });

        html += '</div>';
        return html;
    }

    /**
     * Cookie-Informationen für einen Service abrufen
     * Akzeptiert entweder ein Service-Objekt (mit ggf. eingebetteten `cookies`,
     * z.B. für automatisch erkannte notwendige Dienste) oder eine Service-ID (String).
     */
    getServiceCookieInfo(service) {
        if (service && typeof service === 'object') {
            if (Array.isArray(service.cookies) && service.cookies.length > 0) {
                return service.cookies;
            }
            return this.getServiceCookieInfo(service.id);
        }

        const serviceId = service;
        if (!this.cookieDatabase) return null;

        // Service-Namen für die Datenbank zuordnen
        const serviceMapping = {
            'google_analytics': 'Google Analytics',
            'facebook_pixel': 'Facebook',  // Hinweis: Key in cookie-database.json ist "Facebook", nicht "Facebook Pixel"
            'matomo': 'Matomo',
            'youtube': 'Youtube',  // Hinweis: Key in cookie-database.json ist "Youtube" nicht "YouTube"
            'vimeo': 'Vimeo',
            'dailymotion': 'Dailymotion',
            'spotify': 'Spotify',
            'google_maps': 'Google Maps',
            'soundcloud': 'SoundCloud',
            'instagram': 'Instagram',
            'pinterest': 'Pinterest',
            'pinterest_pixel': 'Pinterest',
            'google_ads': 'Google Ads',
            'google_tag_manager': 'Google Tag Manager',
            'hotjar': 'Hotjar',
            'intercom': 'Intercom',
            'linkedin_insight': 'LinkedIn',
            'tiktok_pixel': 'TikTok',
            'hubspot': 'Hubspot',
            'stripe': 'Stripe',
            'cloudflare_cdn': 'Cloudflare',
            // Zahlungsanbieter: nur eintragen, wenn wirklich geprüfte Cookie-
            // Einträge in der cookie-database.json vorliegen. Anbieter ohne
            // Eintrag hier (Klarna, Amazon Pay, Adyen, Braintree, Square,
            // SumUp, Unzer, Apple/Google Pay) zeigen bewusst KEINE erfundenen
            // Cookie-Namen, sondern den neutralen Hinweis aus
            // getPaymentCookieFallback() - siehe getServicesHTML().
            'paypal': 'PayPal',
            'mollie': 'Mollie',
            // WooCommerce Payments läuft technisch über js.stripe.com - Cookies
            // sind Stripe-Cookies.
            'woocommerce_payments': 'Stripe'
        };

        const dbServiceName = serviceMapping[serviceId];
        if (!dbServiceName || !this.cookieDatabase[dbServiceName]) {
            return null;
        }

        return this.cookieDatabase[dbServiceName];
    }

    /**
     * Neutraler Hinweistext für Zahlungsanbieter, zu denen wir keine eigenen,
     * geprüften Cookie-Namen vorliegen haben (siehe PAYMENT_SERVICE_IDS).
     * Bewusst ohne erfundene Cookie-Namen/Laufzeiten - lieber ehrlich auf die
     * Datenschutzerklärung des Anbieters verweisen als falsche Angaben machen.
     */
    renderPaymentCookieFallback() {
        return `
            <div style="
                margin-top: 12px !important;
                padding-top: 12px !important;
                border-top: 1px solid ${this.design.border?.color || 'rgba(0, 0, 0, 0.1)'} !important;
            ">
                <p style="
                    margin: 0 !important;
                    font-size: 12px !important;
                    color: ${this.design.colors?.text || '#333333'} !important;
                    opacity: 0.75 !important;
                    line-height: 1.5 !important;
                ">
                    ${this.t('paymentCookieFallback')}
                </p>
            </div>
        `;
    }

    /**
     * Cookie-Details für einen Service rendern
     */
    renderServiceCookies(cookies) {
        if (!cookies || cookies.length === 0) return '';

        // Alle Cookies anzeigen
        const displayCookies = cookies;

        let html = `
            <div style="
                margin-top: 12px !important;
                padding-top: 12px !important;
                border-top: 1px solid ${this.design.border?.color || 'rgba(0, 0, 0, 0.1)'} !important;
            ">
                <div style="margin-bottom: 8px !important;">
                    <strong style="
                        font-size: 13px !important;
                        color: ${this.design.colors?.text || '#333333'} !important;
                        display: block !important;
                    ">
                        ${this.t('cookiesUsed')}
                    </strong>
                </div>
                <div style="display: flex !important; flex-direction: column !important; gap: 8px !important;">
        `;

        displayCookies.forEach(cookie => {
            const description = this.getCookieDescription(cookie) || '';
            const retentionPeriod = cookie.retentionPeriod || '';

            html += `
                <div style="
                    padding: 10px !important;
                    background: rgba(0, 0, 0, 0.02) !important;
                    border-radius: 4px !important;
                    font-size: 12px !important;
                ">
                    <div style="display: flex !important; justify-content: space-between !important; margin-bottom: 4px !important;">
                        <strong style="
                            color: ${this.design.colors?.text || '#333333'} !important;
                            font-family: monospace !important;
                            font-size: 12px !important;
                        ">
                            ${cookie.cookie}
                        </strong>
                        <span style="
                            font-size: 11px !important;
                            color: #999999 !important;
                            white-space: nowrap !important;
                            margin-left: 8px !important;
                        ">
                            ${retentionPeriod}
                        </span>
                    </div>
                    ${description ? `
                    <div style="
                        font-size: 12px !important;
                        color: #666666 !important;
                        line-height: 1.4 !important;
                    ">
                        <strong>${this.t('purpose')}</strong> ${description}
                    </div>
                    ` : ''}
                </div>
            `;
        });

        html += `
                </div>
            </div>
        `;

        return html;
    }

    /**
     * Event Listeners für Modal
     */
    attachModalListeners() {
        // Close Button (oben rechts, "×")
        const closeBtn = this.modalElement.querySelector('.pixelmate-modal-close');
        closeBtn?.addEventListener('click', () => this.close());

        // Close Button (Footer, Textbutton neben Auswahl speichern)
        const closeFooterBtn = this.modalElement.querySelector('.pixelmate-modal-close-footer');
        closeFooterBtn?.addEventListener('click', () => this.close());

        // Overlay Click
        this.overlayElement.addEventListener('click', () => this.close());

        // Save Selection
        const saveBtn = this.modalElement.querySelector('.pixelmate-modal-save-selection');
        saveBtn?.addEventListener('click', () => this.saveSelection());

        // Accept All
        const acceptAllBtn = this.modalElement.querySelector('.pixelmate-modal-accept-all');
        acceptAllBtn?.addEventListener('click', () => this.acceptAll());

        const rejectAllBtn = this.modalElement.querySelector('.pixelmate-modal-reject-all');
        rejectAllBtn?.addEventListener('click', () => this.rejectAll());

        // Category Toggle Switches
        const categoryToggles = this.modalElement.querySelectorAll('input[data-category]');
        categoryToggles.forEach(toggle => {
            toggle.addEventListener('change', (e) => {
                this.updateToggleUI(e.target);
                this.handleCategoryToggle(e.target);
            });
        });

        // Service Toggle Switches
        const serviceToggles = this.modalElement.querySelectorAll('input[data-service]');
        serviceToggles.forEach(toggle => {
            toggle.addEventListener('change', (e) => {
                this.updateServiceToggleUI(e.target);
            });
        });

        // Language Switcher: Öffnen/Schließen des Dropdowns sowie das Schließen
        // per Außenklick/Escape sind per Delegation in createModal() verdrahtet
        // (siehe dortiger Kommentar) - hier nur die Sprachauswahl selbst, analog
        // zum bisherigen Muster dieser Methode.
        const langBtns = this.modalElement.querySelectorAll('.pixelmate-lang-btn');
        langBtns.forEach(btn => {
            btn.addEventListener('click', (e) => {
                this.switchLanguage(e.currentTarget.dataset.lang);
            });
        });

        // Tab Switches
        const tabBtns = this.modalElement.querySelectorAll('.pixelmate-tab-btn');
        tabBtns.forEach(btn => {
            btn.addEventListener('click', (e) => {
                const tabName = e.target.dataset.tab;
                this.switchTab(tabName);
            });
        });

        // Service Headers (click to expand cookie details)
        const serviceHeaders = this.modalElement.querySelectorAll('.pixelmate-service-header');
        serviceHeaders.forEach(header => {
            header.addEventListener('click', (e) => {
                e.preventDefault();
                const serviceId = header.dataset.serviceId;
                this.toggleServiceDetails(serviceId);
            });
        });

        // Category Headers (click to expand service list)
        const categoryHeaders = this.modalElement.querySelectorAll('.pixelmate-category-header');
        categoryHeaders.forEach(header => {
            header.addEventListener('click', (e) => {
                e.preventDefault();
                const categoryId = header.dataset.categoryId;
                this.toggleCategoryServices(categoryId);
            });
        });

    }

    /**
     * Dienste-Liste einer Kategorie ein-/ausklappen (animiert via max-height)
     */
    toggleCategoryServices(categoryId) {
        const servicesElement = this.modalElement.querySelector(`.pixelmate-category-services[data-category-id="${categoryId}"]`);
        if (!servicesElement) return;

        const icon = this.modalElement.querySelector(`.pixelmate-category-expand-icon[data-category-id="${categoryId}"]`);
        const isExpanded = !!(servicesElement.style.maxHeight && servicesElement.style.maxHeight !== '0px');

        if (isExpanded) {
            servicesElement.style.maxHeight = '0';
            if (icon) icon.style.transform = 'rotate(-45deg)';
        } else {
            servicesElement.style.maxHeight = '2000px';
            if (icon) icon.style.transform = 'rotate(45deg)';
        }

        if (icon) icon.setAttribute('aria-expanded', String(!isExpanded));
    }

    /**
     * Service-Details ein-/ausklappen (animiert via max-height)
     */
    toggleServiceDetails(serviceId) {
        const detailsElement = this.modalElement.querySelector(`.pixelmate-service-details[data-service-id="${serviceId}"]`);
        if (!detailsElement) return;

        const icon = this.modalElement.querySelector(`.pixelmate-service-expand-icon[data-service-id="${serviceId}"]`);
        const isExpanded = !!(detailsElement.style.maxHeight && detailsElement.style.maxHeight !== '0px');

        if (isExpanded) {
            detailsElement.style.maxHeight = '0';
            if (icon) icon.style.transform = 'rotate(-45deg)';
        } else {
            detailsElement.style.maxHeight = '600px';
            if (icon) icon.style.transform = 'rotate(45deg)';
        }

        if (icon) icon.setAttribute('aria-expanded', String(!isExpanded));
    }

    /**
     * Tab wechseln
     */
    switchTab(tabName) {
        this.activeTab = tabName;

        // Tab-Buttons aktualisieren
        const tabBtns = this.modalElement.querySelectorAll('.pixelmate-tab-btn');
        tabBtns.forEach(btn => {
            const isActive = btn.dataset.tab === tabName;
            const activeColor = this.design.buttons?.accept?.background || '#0073aa';

            if (isActive) {
                btn.style.borderBottomColor = activeColor;
                btn.style.color = activeColor;
            } else {
                btn.style.borderBottomColor = 'transparent';
                btn.style.color = '#666666';
            }
        });

        // Tab-Content aktualisieren
        const tabContents = this.modalElement.querySelectorAll('.pixelmate-tab-content');
        tabContents.forEach(content => {
            const isActive = content.dataset.tabContent === tabName;
            content.style.display = isActive ? 'block' : 'none';
        });

        if (tabName === 'datenauskunft') {
            this.loadDatenauskunft();
        }

    }

    /**
     * Category Toggle UI aktualisieren
     */
    updateToggleUI(checkbox) {
        const slider = checkbox.nextElementSibling;
        if (checkbox.checked) {
            slider.style.backgroundColor = this.design.buttons?.accept?.background || '#0073aa';
        } else {
            slider.style.backgroundColor = '#ccc';
        }

        // Slider-Knopf Position
        const knob = document.createElement('span');
        knob.style.cssText = `
            position: absolute !important;
            content: "" !important;
            height: 20px !important;
            width: 20px !important;
            left: ${checkbox.checked ? '21px' : '2px'} !important;
            bottom: 2px !important;
            background-color: white !important;
            transition: 0.3s !important;
            border-radius: 50% !important;
        `;

        // Alten Knopf entfernen, neuen hinzufügen
        const oldKnob = slider.querySelector('span');
        if (oldKnob) oldKnob.remove();
        slider.appendChild(knob);
    }

    /**
     * Service Toggle UI aktualisieren
     */
    updateServiceToggleUI(checkbox) {
        const slider = checkbox.nextElementSibling;
        if (checkbox.checked) {
            slider.style.backgroundColor = this.design.buttons?.accept?.background || '#0073aa';
        } else {
            slider.style.backgroundColor = '#ccc';
        }

        // Slider-Knopf Position
        const knob = document.createElement('span');
        knob.style.cssText = `
            position: absolute !important;
            content: "" !important;
            height: 20px !important;
            width: 20px !important;
            left: ${checkbox.checked ? '21px' : '2px'} !important;
            bottom: 2px !important;
            background-color: white !important;
            transition: 0.3s !important;
            border-radius: 50% !important;
        `;

        // Alten Knopf entfernen, neuen hinzufügen
        const oldKnob = slider.querySelector('span');
        if (oldKnob) oldKnob.remove();
        slider.appendChild(knob);
    }

    /**
     * Category Toggle Handler - synchronisiert Services mit Category
     */
    handleCategoryToggle(checkbox) {
        const category = checkbox.dataset.category;
        const isChecked = checkbox.checked;

        // Finde alle Service-Toggles in dieser Kategorie und aktualisiere sie
        const categoryElement = checkbox.closest('.pixelmate-category');
        if (categoryElement) {
            const serviceToggles = categoryElement.querySelectorAll('input[data-service]');
            serviceToggles.forEach(serviceToggle => {
                serviceToggle.checked = isChecked;
                this.updateServiceToggleUI(serviceToggle);
            });
        }
    }

    /**
     * Aktuellen Consent laden und Toggles setzen
     */
    loadCurrentConsent() {
        const consent = this.banner.scriptBlocker.consent;
        const serviceConsent = this.banner.scriptBlocker.serviceConsent;

        // Category Toggles setzen
        Object.keys(consent).forEach(category => {
            const checkbox = this.modalElement.querySelector(`input[data-category="${category}"]`);
            if (checkbox && !checkbox.disabled) {
                checkbox.checked = consent[category];
                this.updateToggleUI(checkbox);
            }
        });

        // Service Toggles setzen
        const serviceToggles = this.modalElement.querySelectorAll('input[data-service]');
        serviceToggles.forEach(toggle => {
            const serviceId = toggle.dataset.service;
            // Service-Status aus serviceConsent laden, oder fallback auf Category-Consent
            const isChecked = serviceConsent[serviceId] !== undefined
                ? serviceConsent[serviceId]
                : this.getServiceCategoryConsent(serviceId);
            toggle.checked = isChecked;
            this.updateServiceToggleUI(toggle);
        });
    }

    /**
     * Ermittelt den Consent-Status der Kategorie eines Services
     */
    getServiceCategoryConsent(serviceId) {
        const consent = this.banner.scriptBlocker.consent;

        // Kategorie anhand der bereits eingeordneten Services ermitteln (siehe
        // loadServices()), statt einer separat zu pflegenden Zuordnungsliste.
        const category = Object.keys(this.categories).find(categoryKey =>
            this.categories[categoryKey].services.some(service => service.id === serviceId)
        );
        return category ? consent[category] : false;
    }

    /**
     * Auswahl speichern
     */
    saveSelection() {
        const categories = {};

        // Alle Category-Toggles auslesen
        const categoryToggles = this.modalElement.querySelectorAll('input[data-category]');
        categoryToggles.forEach(toggle => {
            categories[toggle.dataset.category] = toggle.checked;
        });

        // Service-spezifische Toggles auslesen
        const serviceToggles = this.modalElement.querySelectorAll('input[data-service]');
        const serviceStates = {};

        serviceToggles.forEach(toggle => {
            const serviceId = toggle.dataset.service;
            const isChecked = toggle.checked;
            serviceStates[serviceId] = isChecked;
        });

        pmLog('Saving selection:', categories, 'Services:', serviceStates);

        // Prüfen ob CSP aktiviert ist und mindestens eine Kategorie oder Service aktiviert wurde
        const csp = this.banner.csp || {};
        const hasAnyCategoryEnabled = Object.values(categories).some(value => value === true);
        const hasAnyServiceEnabled = Object.values(serviceStates).some(value => value === true);
        const hasAnyConsentEnabled = hasAnyCategoryEnabled || hasAnyServiceEnabled;

        // WICHTIG: Wenn CSP aktiv ist, ist ein Reload NOTWENDIG!
        // Die CSP wird beim initialen Seitenaufruf als <meta>-Tag gesendet und kann
        // nicht nachträglich im Browser geändert werden. Ein Reload ist also IMMER
        // erforderlich, damit der Server die neue CSP-Whitelist sendet – unabhängig
        // von der reloadOnConsentChange-Einstellung.
        if ((csp.active || csp.reloadOnConsentChange) && hasAnyConsentEnabled) {
            // ScriptBlocker überspringen - der Reload übernimmt die Script-Aktivierung sauber
            this.banner.setConsent('custom', { categories, services: serviceStates }, { announceText: this.t('consentSavedAnnouncement') });
            this.banner.trackingDataPreserver.saveTrackingDataBeforeReload();
            window.location.reload();
        } else {
            // Consent aktualisieren und Scripts aktivieren
            const updatedConsent = this.banner.scriptBlocker.updateConsent(categories, serviceStates);
            // announceText: eigener (Modal-)Sprachkontext statt der Banner-Sprache,
            // siehe Kommentar in PixelmateBanner.js setConsent().
            this.banner.setConsent('custom', updatedConsent, { announceText: this.t('consentSavedAnnouncement') });

            // Geblockte Inhalte freischalten, für die jetzt Consent erteilt wurde
            Object.keys(serviceStates).forEach(service => {
                if (serviceStates[service]) {
                    this.banner.contentBlocker.unblockServiceContent(service);
                }
            });

            // Modal schließen
            this.close();

            // Banner verstecken wenn noch sichtbar
            if (this.banner.isVisible) {
                this.banner.hideBanner();
            }
        }
    }

    /**
     * Alle akzeptieren
     */
    acceptAll() {
        // Modal schließen bevor banner.acceptAll() aufgerufen wird (für den Fall ohne Seiten-Reload)
        this.close();
        // Banner-Methode delegieren: setzt Consent-Typ korrekt auf 'accept' statt 'custom',
        // damit die CSP beim nächsten Seitenaufruf deaktiviert wird.
        this.banner.acceptAll();
    }

    /**
     * Alle nicht-notwendigen Cookies ablehnen
     */
    rejectAll() {
        // Modal schließen bevor banner.rejectAll() aufgerufen wird (analog zu acceptAll())
        this.close();
        this.banner.rejectAll();
    }
}
